import { hasReviewedReport, loadReviewedReportHistory, reportDeliveryLabel, recordedReportSentAt, reportDisplayTime } from '../lib/reportHistory'
import ReportDeliveryHistory from './ReportDeliveryHistory'
import PaymentRequestEmail from './PaymentRequestEmail'
import VatJobWorkflow from './VatJobWorkflow'
import JobCataloguePriceRepair from './JobCataloguePriceRepair'
import GeneralJobDiscounts from './GeneralJobDiscounts'
import { useVatIntakePrices } from '../lib/useVatIntakePrices'
import VatIntakeTotals from './VatIntakeTotals'
import { isOutboundTestMode } from '../lib/stagingEnvironment'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import StatusPill from './StatusPill'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { loadWorkshopRows } from '../lib/customerDirectory'
import CustomerCombobox from './CustomerCombobox'
import GeneralChecklistPanel from './GeneralChecklistPanel'
import GeneralAdditionalWorkPanel from './GeneralAdditionalWorkPanel'
import GeneralExtraServiceLinePanel from './GeneralExtraServiceLinePanel'
import JobEvidence from './JobEvidence'
import JobStockUsagePanel from './JobStockUsagePanel'
import {
  fmtDate,
  JOB_FINANCIAL_SELECT,
  jobFinancials,
  JOB_STATUS_OPTIONS,
  jobPriceStatus,
  jobPricingMode,
  jobPaymentActionState,
  jobServiceSummary,
  jobStatusMeta,
  moneyNumber,
  normaliseJobServiceSnapshot,
  normaliseJobStatus,
} from '../lib/utils'
import {
  formatServiceMoney,
  generalJobPricingSummary,
  generalServices as filterGeneralServices,
  serviceBookingLabel,
  serviceBookingType,
  serviceCategoryLabel,
  servicePriceLabel,
  jobServicePriceLabel,
  servicePricingMode,
  serviceVisibility,
} from '../lib/serviceMenu'
import { canAuthoriseChecklistOverride, ensureGeneralJobChecklists } from '../lib/generalChecklists'
import { additionalWorkBillableTotal } from '../lib/generalAdditionalWork'
import { extraServiceLineBillableTotal, extraServiceLineJobScopeRows } from '../lib/generalExtraServiceLines'
import { canOverrideScheduleCapacity, canRunJobLifecycleAction, filterLifecycleStatusOptionsForProfile, lifecycleActionForStatusChange, lifecycleNoteForAction, runJobLifecycleAction, runJobScheduleUpdate, runServiceJobCreateWithSchedule, statusForDirectJobSave } from '../lib/jobLifecycle'
import AdministrativeClosePanel from './AdministrativeClosePanel'
import { generalAdministrativeCloseMissing } from '../lib/administrativeClose'
import { formatPlannedMinutes, plannedMinutesForJob } from '../lib/capacityPlanning'

const TODAY = new Date().toISOString().slice(0, 10)
const GENERAL_REPORT_EMAIL_CONFIRMATION_PHRASE = 'SEND REPORT'

const PRICING_MODE_OPTIONS = [
  ['fixed', 'Fixed price'],
  ['from', 'From price'],
  ['quote_required', 'Quote required'],
]

const PRICE_STATUS_OPTIONS = [
  ['final', 'Final'],
  ['estimate', 'Estimate'],
  ['quote_required', 'Quote required'],
]

const FINDING_CATEGORY_OPTIONS = [
  ['general', 'General'],
  ['brakes', 'Brakes'],
  ['drivetrain', 'Drivetrain'],
  ['wheels', 'Wheels'],
  ['bearings', 'Bearings'],
  ['frame', 'Frame'],
  ['tyres', 'Tyres'],
  ['cockpit', 'Cockpit / controls'],
  ['electrical', 'Electrical'],
  ['other', 'Other'],
]

const FINDING_SEVERITY_OPTIONS = [
  ['info', 'Info'],
  ['advisory', 'Advisory'],
  ['attention', 'Needs attention'],
  ['urgent', 'Urgent'],
  ['safety', 'Safety'],
]

const FINDING_STATUS_OPTIONS = [
  ['open', 'Open'],
  ['monitor', 'Monitor'],
  ['resolved', 'Resolved'],
  ['dismissed', 'Dismissed'],
]

const FINDING_STATUS_ORDER = { open: 0, monitor: 1, resolved: 2, dismissed: 3 }
const FINDING_SEVERITY_ORDER = { safety: 0, urgent: 1, attention: 2, advisory: 3, info: 4 }

const GENERAL_WORKSPACE_TABS = [
  ['work', 'Work'],
  ['checklist', 'Checklist & QC'],
  ['approvals', 'Findings & approvals'],
  ['parts', 'Parts & evidence'],
  ['admin', 'Report & admin'],
]

function normaliseServiceIds(value) {
  if (Array.isArray(value)) return value.map(String).filter(Boolean)
  if (!value || typeof value !== 'string') return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : []
  } catch {
    return value.split(',').map(item => item.trim()).filter(Boolean)
  }
}

function customerName(customer) {
  if (!customer) return 'Unknown customer'
  return [customer.first_name, customer.last_name].filter(Boolean).join(' ') || customer.email || 'Customer'
}

function bikeName(bike) {
  if (!bike) return 'No bike linked'
  return [bike.year, bike.brand, bike.model].filter(Boolean).join(' ') || bike.serial || 'Bike'
}

function fieldAmount(value) {
  const amount = moneyNumber(value)
  return amount > 0 ? amount.toFixed(2) : ''
}

function generalDirectStockBillableTotal(rows = []) {
  return jobFinancials({
    module_key: 'general',
    labour_charge: 0,
    job_additional_work_requests: [],
    job_extra_service_line_requests: [],
    job_stock_usage: rows,
  }).stockPartsSellTotal || 0
}

function labelText(value) {
  return String(value || '').replace(/_/g, ' ')
}

function fmtDateTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
}

function compactPricingBadge(service) {
  const pricingMode = servicePricingMode(service)
  if (pricingMode === 'from') return 'From price'
  if (pricingMode === 'quote_required') return 'Quote'
  return ''
}

function serviceDrivenPricing(services) {
  const summary = generalJobPricingSummary(services)
  const pricingMode = summary.pricing_mode || 'fixed'
  const priceStatus = summary.price_status || 'final'
  const amount = summary.service_total || 0
  return { pricingMode, priceStatus, amount, summary }
}

function generalServiceSnapshot(service) {
  return {
    id: service.id,
    module_key: 'general',
    category: service.category || '',
    category_label: serviceCategoryLabel(service),
    name: service.name || '',
    description: service.description || '',
    base_price: Number(service.base_price || 0),
    pricing_mode: servicePricingMode(service),
    booking_type: serviceBookingType(service),
    expected_labour_minutes: Number(service.expected_labour_minutes || 0),
    price_label: servicePriceLabel(service),
  }
}

function pricingDisplay({ pricingMode, priceStatus, amount }) {
  if (amount <= 0) {
    if (pricingMode === 'quote_required' || priceStatus === 'quote_required') return 'Quote required'
    return pricingMode === 'from' || priceStatus === 'estimate' ? 'Estimate pending' : 'No price recorded'
  }
  if (pricingMode === 'from' || priceStatus === 'estimate' || pricingMode === 'quote_required' || priceStatus === 'quote_required') {
    return `Estimated total ${formatServiceMoney(amount)}`
  }
  return `Total ${formatServiceMoney(amount)}`
}

function emptyFindingForm() {
  return {
    category: 'general',
    severity: 'advisory',
    title: '',
    notes: '',
    estimated_cost: '',
    status: 'open',
    customer_visible: false,
    customer_notes: '',
  }
}

function normaliseFindingSeverity(value) {
  const severity = String(value || 'advisory').trim().toLowerCase()
  return FINDING_SEVERITY_OPTIONS.some(([key]) => key === severity) ? severity : 'advisory'
}

function normaliseFindingStatus(value) {
  const status = String(value || 'open').trim().toLowerCase()
  return FINDING_STATUS_OPTIONS.some(([key]) => key === status) ? status : 'open'
}

function findingSeverityMeta(value) {
  const severity = normaliseFindingSeverity(value)
  const label = FINDING_SEVERITY_OPTIONS.find(([key]) => key === severity)?.[1] || 'Advisory'
  const tone = ['safety', 'urgent'].includes(severity) ? 'red' : severity === 'attention' ? 'blue' : 'muted'
  return { key: severity, label, tone }
}

function findingStatusMeta(value) {
  const status = normaliseFindingStatus(value)
  const label = FINDING_STATUS_OPTIONS.find(([key]) => key === status)?.[1] || 'Open'
  const tone = status === 'resolved' ? 'green' : status === 'dismissed' ? 'muted' : status === 'monitor' ? 'blue' : 'orange'
  return { key: status, label, tone }
}

function findingCategoryLabel(value) {
  const category = String(value || 'general').trim()
  const option = FINDING_CATEGORY_OPTIONS.find(([key]) => key === category.toLowerCase())
  if (option) return option[1]
  return category.replace(/[-_]/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase())
}

function findingAmountField(value) {
  const amount = moneyNumber(value)
  return amount > 0 ? amount.toFixed(2) : ''
}

function sortFindings(rows = []) {
  return [...rows].sort((a, b) => {
    const statusSort = (FINDING_STATUS_ORDER[normaliseFindingStatus(a.status)] ?? 99) - (FINDING_STATUS_ORDER[normaliseFindingStatus(b.status)] ?? 99)
    if (statusSort !== 0) return statusSort
    const severitySort = (FINDING_SEVERITY_ORDER[normaliseFindingSeverity(a.severity)] ?? 99) - (FINDING_SEVERITY_ORDER[normaliseFindingSeverity(b.severity)] ?? 99)
    if (severitySort !== 0) return severitySort
    return new Date(b.created_at || 0) - new Date(a.created_at || 0)
  })
}

function mergeBikeRows(...lists) {
  const map = new Map()
  lists.flat().filter(Boolean).forEach(row => {
    if (!row?.id) return
    const key = String(row.id)
    map.set(key, { ...(map.get(key) || {}), ...row })
  })
  return Array.from(map.values())
}

function GeneralJobFoldout({ title, subtitle, badge, badgeTone = 'muted', defaultOpen = false, forceOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen)

  useEffect(() => {
    if (forceOpen) setOpen(true)
  }, [forceOpen])

  const expanded = open

  return (
    <section className={`general-job-foldout${expanded ? ' open' : ''}`}>
      <button
        type="button"
        className="general-job-foldout-head"
        onClick={() => setOpen(current => !current)}
        aria-expanded={expanded}
      >
        <span>
          <strong>{title}</strong>
          {subtitle && <small>{subtitle}</small>}
        </span>
        <span className="general-job-foldout-meta">
          {badge && <StatusPill as="em" tone={badgeTone}>{badge}</StatusPill>}
          <b>{expanded ? 'Hide' : 'Show'}</b>
        </span>
      </button>
      {expanded && <div className="general-job-foldout-body">{children}</div>}
    </section>
  )
}

function compactText(value, fallback = 'Not set') {
  const text = String(value || '').trim()
  if (!text) return fallback
  return text.split('\n').map(line => line.trim()).filter(Boolean)[0] || fallback
}

function serviceOverviewText(services = [], fallback = '') {
  if (!services.length) return compactText(fallback, 'No service selected')
  const names = services.map(service => service.name).filter(Boolean)
  if (!names.length) return `${services.length} service${services.length === 1 ? '' : 's'} selected`
  const visible = names.slice(0, 2).join(', ')
  const remaining = names.length - 2
  return remaining > 0 ? `${visible} +${remaining} more` : visible
}

function checklistReadinessText(summary = {}) {
  const blockers = summary.blockers || []
  if (blockers.length) return `${blockers.length} QC blocker${blockers.length === 1 ? '' : 's'}`
  const progress = summary.progress || []
  if (!summary.hasChecklists) return 'Service Card setup required'
  if (progress.length && progress.every(row => row.done)) return 'Checklist complete'
  if (progress.length) return progress.map(row => `${row.label}: ${row.completeLabel}`).join(' / ')
  return 'Checklist active'
}

export default function GeneralJobModal({
  workshopId,
  customers = [],
  bikes = [],
  services = [],
  generalServices = [],
  initialData = null,
  customer = null,
  bike = null,
  onClose,
  onSaved,
  onEdit,
  onChanged,
  readOnly = false,
  canCreateGeneralJob = true,
  returnLabel = 'Back',
}) {
  const { profile } = useAuth()
  const outboundTestMode = isOutboundTestMode()
  const isEdit = Boolean(initialData?.id)
  const inheritedServicePrices = isEdit && normaliseJobServiceSnapshot(initialData?.service_snapshot).some(line => line?.vat_source === true)
  const effectiveWorkshopId = workshopId || initialData?.workshop_id || ''
  const sourceServices = generalServices.length ? generalServices : services
  const allGeneralServices = useMemo(() => (
    filterGeneralServices(sourceServices)
      .slice()
      .sort((a, b) => {
        const groupSort = serviceCategoryLabel(a).localeCompare(serviceCategoryLabel(b))
        if (groupSort !== 0) return groupSort
        return (Number(a.sort_order || 0) - Number(b.sort_order || 0)) || String(a.name || '').localeCompare(String(b.name || ''))
      })
  ), [sourceServices])
  const selectableServices = useMemo(() => (
    allGeneralServices.filter(service => service.active !== false && serviceVisibility(service) !== 'archived')
  ), [allGeneralServices])

  const initialServiceIds = normaliseServiceIds(initialData?.service_ids)
  const [form, setForm] = useState(() => ({
    customer_id: initialData?.customer_id || '',
    bike_id: initialData?.bike_id || '',
    service_ids: initialServiceIds,
    service_summary: initialData?.service_summary || '',
    status: normaliseJobStatus(initialData?.status || 'received'),
    date: initialData?.date || TODAY,
    scheduled_date: initialData?.scheduled_date || '',
    due_date: initialData?.due_date || '',
    technician: initialData?.technician || '',
    technician_id: initialData?.technician_id || '',
    scheduled_duration_mins: initialData?.scheduled_duration_mins ?? '',
    physical_location: initialData?.physical_location || '',
    pricing_mode: jobPricingMode(initialData || {}),
    price_status: jobPriceStatus(initialData || {}),
    labour_charge: fieldAmount(initialData?.labour_charge),
    general_work_completed: initialData?.general_work_completed || '',
    general_customer_notes: initialData?.general_customer_notes || '',
    notes: initialData?.notes || '',
    further_work: initialData?.further_work || '',
  }))
  const originalForm = useRef(form)
  const [discountContext, setDiscountContext] = useState(null)
  const [saving, setSaving] = useState(false)
  const [savedForm, setSavedForm] = useState(null)
  const [error, setError] = useState('')
  const [findings, setFindings] = useState([])
  const [findingsLoading, setFindingsLoading] = useState(false)
  const [findingError, setFindingError] = useState('')
  const [findingSaving, setFindingSaving] = useState('')
  const [editingFindingId, setEditingFindingId] = useState(null)
  const [findingForm, setFindingForm] = useState(() => emptyFindingForm())
  const [reportBusy, setReportBusy] = useState('')
  const [reportError, setReportError] = useState('')
  const [reportNotice, setReportNotice] = useState('')
  const [reportVersions, setReportVersions] = useState([])
  const [reportVersionsLoading, setReportVersionsLoading] = useState(false)
  const [reportHistoryError, setReportHistoryError] = useState('')
  const [reportEmailMode, setReportEmailMode] = useState(false)
  const [reportEmailTo, setReportEmailTo] = useState(() => customer?.email || initialData?.customer?.email || '')
  const [reportEmailConfirmationText, setReportEmailConfirmationText] = useState('')
  const [reportEmailConfirmedRecipient, setReportEmailConfirmedRecipient] = useState('')
  const [reportSentAt, setReportSentAt] = useState(() => initialData?.report_sent_at || '')
  const [paymentPatch, setPaymentPatch] = useState({})
  const [paymentBusy, setPaymentBusy] = useState('')
  const [paymentError, setPaymentError] = useState('')
  const [paymentNotice, setPaymentNotice] = useState('')
  const [checklistSummary, setChecklistSummary] = useState({ hasChecklists: false, blockers: [], progress: [] })
  const [additionalWorkBillable, setAdditionalWorkBillable] = useState(() => additionalWorkBillableTotal(initialData?.job_additional_work_requests || []))
  const [additionalWorkSource, setAdditionalWorkSource] = useState(null)
  const [stockBillable, setStockBillable] = useState(() => generalDirectStockBillableTotal(initialData?.job_stock_usage || []))
  const [extraServiceLineBillable, setExtraServiceLineBillable] = useState(() => extraServiceLineBillableTotal(initialData?.job_extra_service_line_requests || []))
  const [extraServiceLineRows, setExtraServiceLineRows] = useState(() => initialData?.job_extra_service_line_requests || [])
  const [extraServiceLineSource, setExtraServiceLineSource] = useState(null)
  const [evidenceRefreshKey, setEvidenceRefreshKey] = useState(0)
  const [serviceSearch, setServiceSearch] = useState('')
  const [expandedServiceCategories, setExpandedServiceCategories] = useState([])
  const previousWorkshop = useRef(effectiveWorkshopId)
  useEffect(() => {
    if (previousWorkshop.current !== effectiveWorkshopId) {
      previousWorkshop.current = effectiveWorkshopId
      setForm(current => ({ ...current, customer_id: '', bike_id: '', service_ids: [] }))
      setModalBikes([])
    }
  }, [effectiveWorkshopId])
  const [modalBikes, setModalBikes] = useState(() => mergeBikeRows(bikes, bike ? [bike] : []))
  const [teamMembers, setTeamMembers] = useState([])
  const [scheduleOverrideOverCapacity, setScheduleOverrideOverCapacity] = useState(false)
  const [workspaceTab, setWorkspaceTab] = useState('work')
  const newJobCloseButtonRef = useRef(null)

  useEffect(() => {
    if (isEdit || readOnly) return undefined
    const previouslyFocused = document.activeElement
    newJobCloseButtonRef.current?.focus()
    function handleKeyDown(event) {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      if (previouslyFocused?.isConnected) previouslyFocused.focus()
    }
  }, [isEdit, onClose, readOnly])

  const selectedServiceIds = useMemo(() => new Set(form.service_ids), [form.service_ids])
  const selectedServiceRows = useMemo(() => (
    allGeneralServices.filter(service => selectedServiceIds.has(String(service.id)))
  ), [allGeneralServices, selectedServiceIds])
  const storedSnapshot = normaliseJobServiceSnapshot(initialData?.service_snapshot)
  const vatIntake = useVatIntakePrices(effectiveWorkshopId, 'general', form.service_ids, !isEdit)
  const displayServices = selectedServiceRows.length ? selectedServiceRows : storedSnapshot
  const selectedPricing = selectedServiceRows.length ? serviceDrivenPricing(selectedServiceRows) : null
  const pricingMode = vatIntake.enquiry ? 'quote_required' : inheritedServicePrices ? form.pricing_mode : selectedPricing?.pricingMode || form.pricing_mode
  const priceStatus = vatIntake.enquiry ? 'quote_required' : inheritedServicePrices ? form.price_status : selectedPricing?.priceStatus || form.price_status
  const amount = moneyNumber(form.labour_charge)
  const serviceBaseAmount = vatIntake.gross ?? (inheritedServicePrices ? moneyNumber(paymentPatch.labour_charge ?? initialData.labour_charge) : selectedServiceRows.length ? moneyNumber(selectedPricing?.amount) : amount)
  const discountRows = discountContext?.adjustments || initialData?.job_price_adjustments || []
  const approvedDiscount = discountRows.filter(row => row.general_discount_id && row.status === 'approved').reduce((sum, row) => sum + Number(row.calculated_amount || 0), 0)
  const internalJobTotal = inheritedServicePrices
    ? moneyNumber(paymentPatch.total_charged ?? initialData.total_charged)
    : moneyNumber(Math.max(0, serviceBaseAmount + additionalWorkBillable + extraServiceLineBillable + stockBillable - approvedDiscount))
  const priceLabel = pricingDisplay({ pricingMode, priceStatus, amount: internalJobTotal })
  const serviceSubtotalLabel = selectedServiceRows.length
    ? pricingDisplay({ pricingMode, priceStatus, amount: serviceBaseAmount })
    : priceLabel
  const approvedExtraServiceRows = useMemo(() => extraServiceLineJobScopeRows(extraServiceLineRows), [extraServiceLineRows])
  const labourChargeFieldValue = selectedServiceRows.length ? fieldAmount(serviceBaseAmount) : form.labour_charge
  const [chosenCustomer, setChosenCustomer] = useState(null)
  const selectedCustomer = [chosenCustomer, customer, ...customers].find(row => row && String(row.id) === String(form.customer_id)) || null
  const defaultReportEmail = selectedCustomer?.email || initialData?.customer?.email || ''
  const effectiveReportSentAt = reportSentAt || initialData?.report_sent_at || ''
  const paymentJob = useMemo(() => ({
    ...(initialData || {}),
    ...paymentPatch,
    module_key: 'general',
  }), [initialData, paymentPatch])
  const [showInvoiceRecords, setShowInvoiceRecords] = useState(false)
  const savedServiceSnapshot = normaliseJobServiceSnapshot(paymentJob.service_snapshot)
  const serviceDisplayPrice = service => jobServicePriceLabel(service, vatIntake, inheritedServicePrices ? savedServiceSnapshot : [])
  const canRestoreCataloguePrice = inheritedServicePrices && !paymentJob.booking_request_id && paymentJob.price_status === 'quote_required'
    && selectedServiceRows.length > 0 && selectedServiceRows.every(service => servicePricingMode(service) === 'fixed' && serviceBookingType(service) !== 'quote_only')
  const handlePricesApplied = job => {
    setForm(current => ({ ...current, pricing_mode: job.pricing_mode, price_status: job.price_status, labour_charge: fieldAmount(job.labour_charge) }))
    setPaymentPatch(current => ({ ...current, ...job }))
    if (onChanged) onChanged()
  }
  const renderInvoiceRecords = () => inheritedServicePrices && process.env.REACT_APP_VAT_BUILD3 === 'true' && (
    <details className="job-invoice-records" onToggle={event => setShowInvoiceRecords(event.currentTarget.open)}>
      <summary>Invoice and payment records</summary>
      {showInvoiceRecords && <VatJobWorkflow job={paymentJob} profile={profile} customer={selectedCustomer} onPricesApplied={handlePricesApplied}
        onDiscountApplied={data => { setPaymentPatch(current => ({ ...current, total_charged: data.tax.gross_minor / 100, financial_revision_key: data.application.financial_revision_key })); handleNestedChanged() }} />}
    </details>
  )
  const renderPriceRepair = () => canRestoreCataloguePrice && <JobCataloguePriceRepair job={paymentJob} profile={profile} onApplied={handlePricesApplied} />
  const availableBikes = modalBikes.filter(row => !form.customer_id || String(row.customer_id || '') === String(form.customer_id))
  const selectedBikeFromForm = modalBikes.find(row => String(row.id || '') === String(form.bike_id)) || null
  const fallbackBikeMatchesCustomer = !form.customer_id || !bike?.customer_id || String(bike.customer_id || '') === String(form.customer_id)
  const selectedBikeFallback = bike && fallbackBikeMatchesCustomer && (!form.bike_id || String(bike.id || '') === String(form.bike_id)) ? bike : null
  const selectedBike = selectedBikeFromForm || selectedBikeFallback || null
  const bikeOptions = selectedBike && !availableBikes.some(row => String(row.id || '') === String(selectedBike.id || ''))
    ? [selectedBike, ...availableBikes]
    : availableBikes
  const selectedTechnicianId = String(form.technician_id || '').trim()
  const selectedTechnician = selectedTechnicianId
    ? teamMembers.find(member => String(member.id || '') === selectedTechnicianId)
    : null
  const plannedScheduleMinutes = plannedMinutesForJob({
    ...(initialData || {}),
    ...form,
    service_snapshot: selectedServiceRows.length ? selectedServiceRows.map(generalServiceSnapshot) : storedSnapshot,
  })
  const canOverrideSchedule = canOverrideScheduleCapacity(profile)
  const lifecycleStatusOptions = useMemo(
    () => filterLifecycleStatusOptionsForProfile(JOB_STATUS_OPTIONS, profile, form.status),
    [profile, form.status]
  )
  const canMarkPaidManually = canRunJobLifecycleAction(profile, 'manual_paid')
  const sortedFindings = useMemo(() => sortFindings(findings), [findings])
  const activeFindingCount = sortedFindings.filter(row => ['open', 'monitor'].includes(normaliseFindingStatus(row.status))).length
  const serviceSearchTerm = serviceSearch.trim().toLowerCase()
  const serviceCategoryRows = useMemo(() => {
    const groups = selectableServices.reduce((acc, service) => {
      const label = serviceCategoryLabel(service)
      if (!acc[label]) acc[label] = []
      acc[label].push(service)
      return acc
    }, {})

    return Object.entries(groups)
      .map(([label, rows]) => ({
        label,
        services: rows,
        selectedCount: rows.filter(service => selectedServiceIds.has(String(service.id))).length,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [selectableServices, selectedServiceIds])
  const expandedCategorySet = useMemo(() => new Set(expandedServiceCategories), [expandedServiceCategories])
  const visibleServiceCategoryRows = useMemo(() => (
    serviceCategoryRows
      .map(group => ({
        ...group,
        services: group.services.filter(service => {
          if (!serviceSearchTerm) return true
          return [
            service.name,
            service.description,
            service.category,
            serviceCategoryLabel(service),
            serviceBookingLabel(service),
            servicePriceLabel(service),
          ].filter(Boolean).join(' ').toLowerCase().includes(serviceSearchTerm)
        }),
      }))
      .filter(group => group.services.length > 0)
  ), [serviceCategoryRows, serviceSearchTerm])
  const visibleServiceCount = visibleServiceCategoryRows.reduce((sum, group) => sum + group.services.length, 0)
  const currentStatus = jobStatusMeta({
    ...(initialData || {}),
    ...(readOnly ? paymentPatch : {}),
    module_key: 'general',
    status: readOnly ? paymentJob.status : form.status,
  })
  const overviewCustomer = customerName(selectedCustomer)
  const overviewBike = selectedBike ? bikeName(selectedBike) : compactText(form.service_summary || initialData?.service_summary, 'No bike linked')
  const overviewServicesBase = serviceOverviewText(displayServices, form.service_summary || jobServiceSummary(initialData || {}))
  const overviewServices = approvedExtraServiceRows.length
    ? `${overviewServicesBase} + ${approvedExtraServiceRows.length} approved extra`
    : overviewServicesBase
  const overviewChecklist = checklistReadinessText(checklistSummary)
  const reportHasContent = Boolean(String(form.general_work_completed || initialData?.general_work_completed || '').trim() || String(form.general_customer_notes || initialData?.general_customer_notes || '').trim())
  const overviewReport = reportBusy
    ? 'Report preparing'
    : effectiveReportSentAt
      ? `Sent ${fmtDateTime(effectiveReportSentAt)}`
      : reportNotice
        ? 'Report saved'
        : reportHasContent ? 'Report notes started' : 'Report notes empty'
  const overviewSchedule = [
    form.scheduled_date ? fmtDate(form.scheduled_date) : 'Needs booked-in date',
    selectedTechnician?.name || form.technician || 'Unassigned',
    plannedScheduleMinutes ? formatPlannedMinutes(plannedScheduleMinutes) : 'Time required',
  ].join(' / ')
  const workspacePayment = jobPaymentActionState({
    ...paymentJob,
    pricing_mode: pricingMode,
    price_status: priceStatus,
  }, { totalCost: internalJobTotal }).payment
  const workspaceNextAction = (() => {
    const status = normaliseJobStatus(form.status)
    if (status === 'completed') return { title: 'Job complete', detail: currentStatus.nextAction }
    if (status === 'ready_for_collection') return { title: currentStatus.nextAction, detail: 'Existing payment and collection safeguards remain in force.' }
    if (checklistSummary.blockers?.length) return { title: 'Clear checklist and QC blockers', detail: overviewChecklist }
    if (priceStatus !== 'final') return { title: 'Review estimate and approval', detail: priceLabel }
    if (!reportHasContent) return { title: 'Record completed work', detail: 'Add customer-facing service and handover information.' }
    return { title: 'Save progress', detail: 'Keep the General job record current before leaving the workspace.' }
  })()

  useEffect(() => {
    setReportEmailTo(defaultReportEmail)
    setReportEmailConfirmationText('')
    setReportEmailConfirmedRecipient('')
  }, [defaultReportEmail, initialData?.id])

  useEffect(() => {
    setReportSentAt(initialData?.report_sent_at || '')
  }, [initialData?.id, initialData?.report_sent_at])

  useEffect(() => {
    let cancelled = false

    async function loadReportVersions() {
      if (!initialData?.id || !effectiveWorkshopId) {
        setReportVersions([])
        setReportVersionsLoading(false)
        setReportHistoryError('')
        return
      }

      setReportVersionsLoading(true)
      setReportHistoryError('')
      const { data, error: loadError } = hasReviewedReport(initialData) ? await loadReviewedReportHistory(supabase, initialData) : await supabase
        .from('job_report_versions')
        .select('*')
        .eq('workshop_id', effectiveWorkshopId)
        .eq('job_id', initialData.id)
        .eq('report_type', 'general')
        .order('version_number', { ascending: false })

      if (cancelled) return
      if (loadError) {
        setReportVersions([])
        setReportHistoryError(loadError.message || 'Could not load saved General reports.')
      } else {
        setReportVersions(data || [])
      }
      setReportVersionsLoading(false)
    }

    loadReportVersions()
    return () => { cancelled = true }
  }, [initialData?.id, effectiveWorkshopId])

  useEffect(() => {
    setAdditionalWorkBillable(additionalWorkBillableTotal(initialData?.job_additional_work_requests || []))
  }, [initialData?.id, initialData?.job_additional_work_requests])

  useEffect(() => {
    setStockBillable(generalDirectStockBillableTotal(initialData?.job_stock_usage || []))
  }, [initialData?.id, initialData?.job_stock_usage])

  useEffect(() => {
    const rows = initialData?.job_extra_service_line_requests || []
    setExtraServiceLineRows(rows)
    setExtraServiceLineBillable(extraServiceLineBillableTotal(rows))
  }, [initialData?.id, initialData?.job_extra_service_line_requests])

  useEffect(() => {
    setModalBikes(current => mergeBikeRows(current, bikes, bike ? [bike] : []))
  }, [bikes, bike])

  useEffect(() => {
    let cancelled = false

    async function loadTeamMembers() {
      if (!effectiveWorkshopId) {
        setTeamMembers([])
        return
      }

      const { data } = await supabase
        .from('profiles')
        .select('id, full_name, role, weekly_bookable_hours, calendar_colour')
        .eq('workshop_id', effectiveWorkshopId)
        .order('full_name')

      if (cancelled) return

      const seen = new Set()
      const members = (data || [])
        .map(member => ({
          ...member,
          name: String(member.full_name || '').trim(),
        }))
        .filter(member => member.name)
        .filter(member => {
          const key = member.id || member.name.toLowerCase()
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })
      setTeamMembers(members)
    }

    loadTeamMembers()
    return () => { cancelled = true }
  }, [effectiveWorkshopId])

  useEffect(() => {
    const customerId = String(form.customer_id || '').trim()
    if (!effectiveWorkshopId || !customerId) return undefined

    const controller = new AbortController()
    loadWorkshopRows(supabase, 'bikes', effectiveWorkshopId, '*', controller.signal, customerId)
      .then(data => {
        if (!controller.signal.aborted) setModalBikes(current => mergeBikeRows(current, data))
      }).catch(() => { if (!controller.signal.aborted) setError('Could not refresh customer bikes. Reopen the job to retry.') })
    return () => controller.abort()
  }, [effectiveWorkshopId, form.customer_id])

  useEffect(() => {
    setPaymentPatch({})
    setPaymentError('')
    setPaymentNotice('')
  }, [initialData?.id])

  useEffect(() => {
    let cancelled = false

    async function loadFindings() {
      if (!initialData?.id || !effectiveWorkshopId) {
        setFindings([])
        setFindingsLoading(false)
        setFindingError('')
        return
      }

      setFindingsLoading(true)
      setFindingError('')
      const { data, error: loadError } = await supabase
        .from('job_findings')
        .select('*')
        .eq('workshop_id', effectiveWorkshopId)
        .eq('job_id', initialData.id)
        .order('created_at', { ascending: false })

      if (cancelled) return
      if (loadError) {
        setFindings([])
        setFindingError(loadError.message || 'Could not load internal findings.')
      } else {
        setFindings(data || [])
      }
      setFindingsLoading(false)
    }

    loadFindings()
    return () => { cancelled = true }
  }, [initialData?.id, effectiveWorkshopId])

  useEffect(() => {
    const selectedCategories = selectedServiceRows.map(service => serviceCategoryLabel(service))
    if (!selectedCategories.length) return
    setExpandedServiceCategories(current => {
      const next = new Set(current)
      selectedCategories.forEach(label => next.add(label))
      return next.size === current.length ? current : Array.from(next)
    })
  }, [selectedServiceRows])

  useEffect(() => {
    const validLabels = new Set(serviceCategoryRows.map(group => group.label))
    setExpandedServiceCategories(current => {
      const next = current.filter(label => validLabels.has(label))
      return next.length === current.length ? current : next
    })
  }, [serviceCategoryRows])

  function setField(key, value) {
    setForm(current => ({ ...current, [key]: value }))
  }

  function setTechnicianAssignment(value) {
    const member = teamMembers.find(row => String(row.id || '') === String(value || ''))
    setForm(current => ({
      ...current,
      technician_id: value || '',
      technician: member?.name || '',
    }))
  }

  function changeCustomer(customerId) {
    setForm(current => {
      const keepBike = modalBikes.some(row => (
        String(row.id || '') === String(current.bike_id)
        && String(row.customer_id || '') === String(customerId)
      ))
      return { ...current, customer_id: customerId, bike_id: keepBike ? current.bike_id : '' }
    })
  }

  function toggleService(serviceId) {
    setForm(current => {
      const ids = current.service_ids.includes(serviceId)
        ? current.service_ids.filter(id => id !== serviceId)
        : [...current.service_ids, serviceId]
      const rows = allGeneralServices.filter(service => ids.includes(String(service.id)))
      const driven = serviceDrivenPricing(rows)
      return {
        ...current,
        service_ids: ids,
        service_summary: rows.map(service => service.name).filter(Boolean).join('\n'),
        pricing_mode: rows.length ? driven.pricingMode : current.pricing_mode,
        price_status: rows.length ? driven.priceStatus : current.price_status,
        labour_charge: rows.length ? fieldAmount(driven.amount) : current.labour_charge,
      }
    })
  }

  function toggleServiceCategory(label) {
    setExpandedServiceCategories(current => (
      current.includes(label)
        ? current.filter(item => item !== label)
        : [...current, label]
    ))
  }

  function renderServicePickerRow(service) {
    const checked = selectedServiceIds.has(String(service.id))
    const bookingLabel = serviceBookingType(service) === 'assessment_first' ? '' : serviceBookingLabel(service)
    const pricingBadge = compactPricingBadge(service)

    return (
      <label key={service.id} className={`general-service-picker-row${checked ? ' selected' : ''}`}>
        <input
          type="checkbox"
          checked={checked}
          onChange={() => toggleService(String(service.id))}
          disabled={saving}
        />
        <span className="general-service-picker-main">
          <strong>{service.name}</strong>
          <small>{serviceCategoryLabel(service)}</small>
          {(bookingLabel || pricingBadge) && (
            <span className="general-service-picker-badges">
              {bookingLabel && <span className="booking-service-badge">{bookingLabel}</span>}
              {pricingBadge && <span className="booking-service-badge">{pricingBadge}</span>}
            </span>
          )}
        </span>
        <em>{serviceDisplayPrice(service)}</em>
      </label>
    )
  }

  function renderJobOverview({ price = priceLabel } = {}) {
    return (
      <div className="general-job-overview">
        <div className="job-modal-summary general-job-summary">
          <div className="job-modal-summary-item">
            <span>Customer</span>
            <strong>{overviewCustomer}</strong>
          </div>
          <div className="job-modal-summary-item">
            <span>Bike / Context</span>
            <strong>{overviewBike}</strong>
          </div>
          <div className="job-modal-summary-item">
            <span>Status</span>
            <strong><StatusPill tone={currentStatus.tone}>{currentStatus.label}</StatusPill></strong>
          </div>
          <div className="job-modal-summary-item">
            <span>Schedule</span>
            <strong>{overviewSchedule}</strong>
          </div>
          <div className="job-modal-summary-item">
            <span>Services</span>
            <strong>{overviewServices}</strong>
          </div>
          <div className="job-modal-summary-item">
            <span>Price</span>
            <strong>{price}</strong>
          </div>
          <div className="job-modal-summary-item">
            <span>Checklist</span>
            <strong>{overviewChecklist}</strong>
          </div>
          <div className="job-modal-summary-item">
            <span>Report</span>
            <strong>{overviewReport}</strong>
          </div>
        </div>
      </div>
    )
  }

  function renderApprovedExtraServiceScope() {
    if (!approvedExtraServiceRows.length) return null
    return (
      <div className="general-approved-extra-services">
        <div className="general-approved-extra-services-head">
          <span>Approved extra services</span>
          <strong>{approvedExtraServiceRows.length} mid-job service{approvedExtraServiceRows.length === 1 ? '' : 's'}</strong>
        </div>
        <div className="general-approved-extra-service-list">
          {approvedExtraServiceRows.map(row => (
            <div key={row.id || `${row.name}-${row.category}`} className="general-approved-extra-service-chip">
              <span>{row.name}</span>
              {row.category && <em>{row.category}</em>}
              <b>{formatServiceMoney(row.amount)}</b>
            </div>
          ))}
        </div>
      </div>
    )
  }

  function changePricingMode(value) {
    setForm(current => ({
      ...current,
      pricing_mode: value,
      price_status: value === 'quote_required' ? 'quote_required' : value === 'from' ? 'estimate' : current.price_status,
    }))
  }

  async function save() {
    if (saving) return
    if (vatIntake.blocked) { setError(vatIntake.error || 'Wait for reviewed prices.'); return }
    setSavedForm(null)
    if (!isEdit && !canCreateGeneralJob) {
      setError('General Workshop jobs can only be created when the module is enabled for this workshop.')
      return
    }
    if (!form.customer_id) {
      setError('Select a customer.')
      return
    }
    if (!selectedServiceRows.length && !String(form.service_summary || '').trim()) {
      setError('Select at least one General service or enter the requested work.')
      return
    }
    if (form.status === 'ready_for_collection' && checklistSummary.hasChecklists && checklistSummary.blockers.length > 0) {
      setError('Complete Final QC or add an authorised override before marking this General job Ready for Collection.')
      return
    }

    if (inheritedServicePrices && (
      JSON.stringify([...form.service_ids].sort()) !== JSON.stringify([...normaliseServiceIds(paymentJob.service_ids)].sort())
      || form.pricing_mode !== jobPricingMode(paymentJob)
      || form.price_status !== jobPriceStatus(paymentJob)
      || moneyNumber(form.labour_charge) !== moneyNumber(paymentJob.labour_charge)
    )) {
      setError('Recorded service prices need an explicit reviewed quote before they can change.')
      return
    }

    setSaving(true)
    setError('')

    const requestedStatus = form.status || 'received'
    const previousStatus = isEdit ? normaliseJobStatus(initialData?.status || 'received') : 'received'
    const lifecycleAction = lifecycleActionForStatusChange(previousStatus, requestedStatus)
    const effectivePricing = vatIntake.enquiry ? { pricingMode: 'quote_required', priceStatus: 'quote_required', amount: 0 } : inheritedServicePrices
      ? { pricingMode: paymentJob.pricing_mode, priceStatus: paymentJob.price_status }
      : selectedServiceRows.length
      ? serviceDrivenPricing(selectedServiceRows)
      : { pricingMode: form.pricing_mode, priceStatus: form.price_status, amount: moneyNumber(form.labour_charge) }
    const savedAmount = vatIntake.gross ?? (inheritedServicePrices ? moneyNumber(paymentJob.labour_charge) : selectedServiceRows.length ? moneyNumber(effectivePricing.amount) : moneyNumber(form.labour_charge))
    const savedTotal = inheritedServicePrices ? paymentJob.total_charged : moneyNumber(Math.max(0, savedAmount + additionalWorkBillable + extraServiceLineBillable + stockBillable - approvedDiscount))
    const payload = {
      workshop_id: effectiveWorkshopId,
      module_key: 'general',
      customer_id: form.customer_id,
      bike_id: form.bike_id || null,
      date: form.date || TODAY,
      scheduled_date: form.scheduled_date || null,
      due_date: form.due_date || null,
      status: statusForDirectJobSave(previousStatus, requestedStatus, 'received'),
      technician_id: form.technician_id || null,
      technician: selectedTechnician ? selectedTechnician.name : String(form.technician || '').trim(),
      physical_location: String(form.physical_location || '').trim(),
      service_ids: form.service_ids,
      service_summary: String(form.service_summary || '').trim(),
      service_snapshot: inheritedServicePrices ? paymentJob.service_snapshot : selectedServiceRows.map(generalServiceSnapshot),
      pricing_mode: effectivePricing.pricingMode,
      price_status: effectivePricing.priceStatus,
      labour_charge: savedAmount,
      total_charged: savedTotal,
      general_work_completed: String(form.general_work_completed || '').trim(),
      general_customer_notes: String(form.general_customer_notes || '').trim(),
      notes: String(form.notes || '').trim(),
      further_work: String(form.further_work || '').trim(),
    }

    const directPayload = (({ scheduled_date, technician, technician_id, ...rest }) => rest)(payload)
    const scheduleChanged = isEdit && (
      String(initialData?.scheduled_date || '') !== String(payload.scheduled_date || '')
      || String(initialData?.technician_id || '') !== String(payload.technician_id || '')
      || String(initialData?.technician || '') !== String(payload.technician || '')
    )

    let result
    try {
      result = isEdit
        ? await supabase
          .from('jobs')
          .update(directPayload)
          .eq('id', initialData.id)
          .select('*, job_price_adjustments(*)')
          .single()
        : { data: (await runServiceJobCreateWithSchedule(supabase, directPayload, {
          ...vatIntake.options,
          scheduledDate: payload.scheduled_date,
          technicianId: payload.technician_id,
          overrideOverCapacity: scheduleOverrideOverCapacity,
          note: 'General job initial schedule set from the job card.',
        }))?.job, error: null }
    } catch (err) {
      setSaving(false)
      setError(err.message || 'Could not save General job.')
      return
    }

    if (result.error) {
      setSaving(false)
      setError(result.error.message || 'Could not save General job.')
      return
    }
    let savedJob = result.data
    if (scheduleChanged && savedJob?.id) {
      try {
        const scheduleResult = await runJobScheduleUpdate(supabase, savedJob.id, {
          scheduledDate: payload.scheduled_date,
          technicianId: payload.technician_id,
          overrideOverCapacity: scheduleOverrideOverCapacity,
          note: 'General job schedule updated from the job card.',
        })
        savedJob = scheduleResult?.job || savedJob
      } catch (err) {
        setSaving(false)
        setError(err.message || 'Job saved, but the schedule change was blocked.')
        return
      }
    }
    try {
      await ensureGeneralJobChecklists(supabase, savedJob?.id)
    } catch (err) {
      console.warn('General checklist creation check failed:', err.message)
    }
    if (lifecycleAction && savedJob?.id) {
      try {
        const lifecycleResult = await runJobLifecycleAction(supabase, lifecycleAction, savedJob.id, {
          note: lifecycleNoteForAction(lifecycleAction, { ...savedJob, total_charged: savedTotal }),
          reason: lifecycleNoteForAction(lifecycleAction, { ...savedJob, total_charged: savedTotal }),
        })
        const refreshed = await supabase
          .from('jobs')
          .select('*, job_price_adjustments(*)')
          .eq('id', savedJob.id)
          .single()
        savedJob = refreshed.data || lifecycleResult?.job || savedJob
      } catch (err) {
        setError(err.message || 'Job saved, but the lifecycle action was blocked.')
        setSaving(false)
        return
      }
    }
    setSaving(false)
    setSavedForm(form)
    if (onSaved) onSaved(savedJob)
  }

  function startAdditionalWorkFromChecklist(source) {
    setAdditionalWorkSource({ ...source, nonce: Date.now() })
  }

  function startExtraServiceLineFromChecklist(source) {
    setExtraServiceLineSource({ ...source, nonce: Date.now() })
  }

  function startAdditionalWorkFromFinding(finding) {
    setAdditionalWorkSource({
      source_type: 'finding',
      finding_id: finding.id,
      title: finding.title || '',
      customer_explanation: finding.customer_notes || '',
      internal_note: finding.notes || '',
      nonce: Date.now(),
    })
  }

  function handleNestedChanged() {
    setEvidenceRefreshKey(value => value + 1)
    if (onChanged) onChanged()
  }

  function mergeReportVersion(version) {
    if (!version?.id) return
    setReportVersions(current => [version, ...current.filter(row => row.id !== version.id)]
      .sort((a, b) => Number(b.version_number || 0) - Number(a.version_number || 0)))
  }

  async function openGeneralReportVersion(version) {
    if (!version?.storage_path) return
    setReportBusy(`open-${version.id}`)
    setReportError('')
    setReportHistoryError('')
    try {
      const { data, error: signedError } = await supabase.storage
        .from(version.storage_bucket || 'job-reports')
        .createSignedUrl(version.storage_path, 60 * 10)
      if (signedError) throw signedError
      if (data?.signedUrl) window.open(data.signedUrl, '_blank', 'noopener,noreferrer')
    } catch (err) {
      setReportHistoryError(err.message || 'Could not open saved General report.')
    } finally {
      setReportBusy('')
    }
  }

  async function handleStockUsageChanged() {
    setEvidenceRefreshKey(value => value + 1)
    if (!initialData?.id) {
      if (onChanged) onChanged()
      return
    }

    try {
      const { data, error: loadError } = await supabase
        .from('jobs')
        .select(JOB_FINANCIAL_SELECT)
        .eq('id', initialData.id)
        .single()
      if (loadError) throw loadError

      const financials = jobFinancials({ ...(data || {}), module_key: 'general' })
      setStockBillable(financials.stockPartsSellTotal || 0)

      const total = moneyNumber(financials.totalCost)
      const amountPence = Math.round(total * 100)
      const storedPaymentAmountPence = Number(data?.payment_amount_pence || 0)
      const storedJobAmountPence = Math.round(Math.max(0, Number(data?.total_charged) || 0) * 100)
      const paymentAlreadyPaid = data?.payment_status === 'paid' || Boolean(data?.payment_paid_at)
      const hasExistingPaymentLink = Boolean(
        data?.payment_link
        || data?.stripe_checkout_session_id
        || data?.stripe_payment_intent_id
        || data?.stripe_payment_link_id
        || data?.payment_requested_at
      )
      const existingPaymentAmountMatches = storedPaymentAmountPence > 0
        ? storedPaymentAmountPence === amountPence
        : storedJobAmountPence === amountPence
      const paymentRefreshFields = !paymentAlreadyPaid && hasExistingPaymentLink && amountPence > 0 && !existingPaymentAmountMatches
        ? {
            payment_link: null,
            stripe_payment_link_id: null,
            stripe_checkout_session_id: null,
            stripe_payment_intent_id: null,
            payment_provider: null,
            payment_amount_pence: null,
            payment_status: 'not_requested',
            payment_requested_at: null,
            payment_reminder_sent_at: null,
            payment_refresh_required: true,
            payment_refresh_reason: 'General Workshop job total changed after stock usage was updated. Generate a fresh payment link before requesting payment.',
          }
        : {}

      const { data: synced, error: updateError } = await supabase
        .from('jobs')
        .update({ total_charged: total, ...paymentRefreshFields })
        .eq('id', initialData.id)
        .select(JOB_FINANCIAL_SELECT)
        .single()
      if (updateError) throw updateError

      setStockBillable(generalDirectStockBillableTotal(synced?.job_stock_usage || []))
      setPaymentPatch({
        payment_link: synced.payment_link,
        stripe_payment_link_id: synced.stripe_payment_link_id,
        stripe_checkout_session_id: synced.stripe_checkout_session_id,
        stripe_payment_intent_id: synced.stripe_payment_intent_id,
        payment_provider: synced.payment_provider,
        payment_amount_pence: synced.payment_amount_pence,
        payment_status: synced.payment_status,
        payment_requested_at: synced.payment_requested_at,
        payment_reminder_sent_at: synced.payment_reminder_sent_at,
        payment_refresh_required: synced.payment_refresh_required,
        payment_refresh_reason: synced.payment_refresh_reason,
      })
      if (onChanged) onChanged()
    } catch (err) {
      setError(err.message || 'Could not refresh General stock totals.')
    }
  }

  const handleExtraServiceLineRowsChange = useCallback((rows = []) => {
    const safeRows = Array.isArray(rows) ? rows : []
    setExtraServiceLineRows(safeRows)
    setExtraServiceLineBillable(extraServiceLineBillableTotal(safeRows))
  }, [])

  async function sessionTokenForPayment() {
    const { data } = await supabase.auth.getSession()
    const token = data?.session?.access_token
    if (!token) throw new Error('Please sign in again before taking payment actions.')
    return token
  }

  async function createGeneralPaymentLink() {
    if (outboundTestMode) {
      setPaymentError('Payment creation is disabled in test mode.')
      return
    }
    if (!initialData?.id) {
      setPaymentError('Save the General job before creating a payment link.')
      return
    }
    setPaymentBusy('create')
    setPaymentError('')
    setPaymentNotice('')
    try {
      const token = await sessionTokenForPayment()
      const res = await fetch('/.netlify/functions/create-payment-link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ jobId: initialData.id }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Could not create payment link.')
      setPaymentPatch(current => ({
        ...current,
        payment_link: data.url,
        payment_status: data.payment_status || 'requested',
        payment_requested_at: new Date().toISOString(),
        stripe_checkout_session_id: data.checkout_session_id || current.stripe_checkout_session_id || initialData?.stripe_checkout_session_id || '',
        payment_amount_pence: data.payment_amount_pence || current.payment_amount_pence || initialData?.payment_amount_pence || null,
        payment_refresh_required: false,
        payment_refresh_reason: '',
      }))
      setPaymentNotice('Payment link ready — customer not contacted.')
      if (onChanged) onChanged()
    } catch (err) {
      setPaymentError(err.message || 'Could not create payment link.')
    } finally {
      setPaymentBusy('')
    }
  }

  async function sendGeneralPaymentReminder() {
    if (outboundTestMode) {
      setPaymentError('Outbound payment messages are disabled in test mode.')
      return
    }
    const to = selectedCustomer?.email || ''
    if (!to) {
      setPaymentError('Add a customer email before sending a payment reminder.')
      return
    }
    setPaymentBusy('reminder')
    setPaymentError('')
    setPaymentNotice('')
    try {
      const token = await sessionTokenForPayment()
      const firstSend = !paymentJob.payment_reminder_sent_at
      const res = await fetch('/.netlify/functions/send-payment-reminder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ jobId: initialData.id, to }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Payment reminder failed.')
      setPaymentPatch(current => ({
        ...current,
        payment_status: 'requested',
        payment_reminder_sent_at: data.payment_reminder_sent_at || new Date().toISOString(),
        report_sent_at: data.report_sent_at || new Date().toISOString(),
      }))
      if (data.report_version) mergeReportVersion(data.report_version)
      setReportSentAt(data.report_sent_at || new Date().toISOString())
      setPaymentNotice(firstSend ? `Payment request and report emailed to ${to}.` : `Payment reminder and report emailed to ${to}.`)
      if (data.warning) setPaymentError(data.warning)
      if (onChanged) onChanged()
    } catch (err) {
      setPaymentError(err.message || 'Payment reminder failed.')
    } finally {
      setPaymentBusy('')
    }
  }

  async function copyGeneralPaymentLink(link) {
    if (!link) return
    try {
      await navigator.clipboard.writeText(link)
      setPaymentNotice('Payment link copied.')
      setPaymentError('')
    } catch {
      setPaymentError('Could not copy payment link in this browser.')
    }
  }

  async function markGeneralPaidManually(amount) {
    if (!initialData?.id) {
      setPaymentError('Save the General job before marking payment paid.')
      return
    }
    const amountPence = Math.round(Math.max(0, Number(amount || 0)) * 100)
    setPaymentBusy('paid')
    setPaymentError('')
    setPaymentNotice('')
    try {
      const data = await runJobLifecycleAction(supabase, 'manual_paid', initialData.id, {
        method: 'manual',
        reference: paymentJob.payment_reference || 'Marked paid in Rebound',
        amountPence,
        note: 'General job payment marked paid manually from the job card.',
      })
      const nextJob = data?.job || data
      setPaymentPatch(current => ({
        ...current,
        payment_link: nextJob?.payment_link || null,
        stripe_payment_link_id: nextJob?.stripe_payment_link_id || '',
        stripe_checkout_session_id: nextJob?.stripe_checkout_session_id || '',
        stripe_payment_intent_id: nextJob?.stripe_payment_intent_id || '',
        payment_provider: nextJob?.payment_provider || 'manual',
        payment_amount_pence: nextJob?.payment_amount_pence || amountPence,
        payment_status: nextJob?.payment_status || 'paid',
        payment_paid_at: nextJob?.payment_paid_at || new Date().toISOString(),
        payment_method: nextJob?.payment_method || 'manual',
        payment_reference: nextJob?.payment_reference || 'Marked paid in Rebound',
        payment_refresh_required: false,
        payment_refresh_reason: '',
      }))
      setPaymentNotice('Payment marked as paid.')
      if (onChanged) onChanged()
    } catch (err) {
      setPaymentError(err.message || 'Could not mark payment paid.')
    } finally {
      setPaymentBusy('')
    }
  }

  function setFindingField(key, value) {
    setFindingForm(current => ({ ...current, [key]: value }))
  }

  function startEditFinding(finding) {
    setEditingFindingId(finding.id)
    setFindingError('')
    setFindingForm({
      category: finding.category || 'general',
      severity: normaliseFindingSeverity(finding.severity),
      title: finding.title || '',
      notes: finding.notes || '',
      estimated_cost: findingAmountField(finding.estimated_cost),
      status: normaliseFindingStatus(finding.status),
      customer_visible: Boolean(finding.customer_visible),
      customer_notes: finding.customer_notes || '',
    })
  }

  function cancelFindingEdit() {
    setEditingFindingId(null)
    setFindingError('')
    setFindingForm(emptyFindingForm())
  }

  function mergeFinding(row) {
    setFindings(current => {
      const exists = current.some(item => item.id === row.id)
      return exists ? current.map(item => item.id === row.id ? row : item) : [row, ...current]
    })
  }

  async function saveFinding() {
    if (!initialData?.id) {
      setFindingError('Save the General job before adding internal findings.')
      return
    }

    const title = String(findingForm.title || '').trim()
    if (!title) {
      setFindingError('Add a finding title.')
      return
    }
    const customerNotes = String(findingForm.customer_notes || '').trim()
    if (findingForm.customer_visible && !customerNotes) {
      setFindingError('Add customer-facing notes before including this finding in the customer report.')
      return
    }

    const payload = {
      workshop_id: effectiveWorkshopId,
      job_id: initialData.id,
      category: String(findingForm.category || 'general').trim() || 'general',
      severity: normaliseFindingSeverity(findingForm.severity),
      title,
      notes: String(findingForm.notes || '').trim(),
      estimated_cost: moneyNumber(findingForm.estimated_cost),
      status: normaliseFindingStatus(findingForm.status),
      customer_visible: Boolean(findingForm.customer_visible),
      customer_notes: customerNotes,
    }

    setFindingSaving(editingFindingId || 'new')
    setFindingError('')

    const result = editingFindingId
      ? await supabase
        .from('job_findings')
        .update(payload)
        .eq('workshop_id', effectiveWorkshopId)
        .eq('job_id', initialData.id)
        .eq('id', editingFindingId)
        .select('*')
        .single()
      : await supabase
        .from('job_findings')
        .insert(payload)
        .select('*')
        .single()

    setFindingSaving('')
    if (result.error) {
      setFindingError(result.error.message || 'Could not save internal finding.')
      return
    }

    mergeFinding(result.data)
    cancelFindingEdit()
  }

  async function updateFindingStatus(finding, status) {
    const nextStatus = normaliseFindingStatus(status)
    if (!finding?.id || nextStatus === normaliseFindingStatus(finding.status)) return

    setFindingSaving(finding.id)
    setFindingError('')
    const { data, error: updateError } = await supabase
      .from('job_findings')
      .update({ status: nextStatus })
      .eq('workshop_id', effectiveWorkshopId)
      .eq('job_id', initialData.id)
      .eq('id', finding.id)
      .select('*')
      .single()

    setFindingSaving('')
    if (updateError) {
      setFindingError(updateError.message || 'Could not update finding status.')
      return
    }
    mergeFinding(data)
  }

  async function requestGeneralReport(mode) {
    if (!initialData?.id) throw new Error('Save the General job before creating a customer report.')
    const { data: sessionData } = await supabase.auth.getSession()
    const token = sessionData?.session?.access_token
    if (!token) throw new Error('Please sign in again before creating a customer report.')

    const res = await fetch('/.netlify/functions/create-general-report-version', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ jobId: initialData.id, mode }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || 'Could not create General report.')
    return data
  }

  async function previewGeneralReport() {
    const previewWindow = window.open('', '_blank')
    if (previewWindow?.document) {
      previewWindow.document.write('<!doctype html><title>General Workshop Report</title><p style="font-family:Arial,sans-serif;padding:24px">Preparing report preview...</p>')
    }

    setReportBusy('preview')
    setReportError('')
    setReportNotice('')
    try {
      const data = await requestGeneralReport('preview')
      if (previewWindow?.document) {
        previewWindow.document.open()
        previewWindow.document.write(data.html || '')
        previewWindow.document.close()
      } else if (data.html) {
        const blobUrl = URL.createObjectURL(new Blob([data.html], { type: 'text/html' }))
        window.open(blobUrl, '_blank', 'noopener,noreferrer')
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000)
      }
    } catch (err) {
      if (previewWindow?.document) {
        previewWindow.document.open()
        previewWindow.document.write('<!doctype html><title>General Workshop Report</title><p style="font-family:Arial,sans-serif;padding:24px">Could not preview General report.</p>')
        previewWindow.document.close()
      }
      setReportError(err.message || 'Could not preview General report.')
    } finally {
      setReportBusy('')
    }
  }

  async function saveGeneralReportVersion() {
    setReportBusy('save')
    setReportError('')
    setReportNotice('')
    try {
      const data = await requestGeneralReport('save')
      if (data.report_version) mergeReportVersion(data.report_version)
      setReportNotice(`Saved General report v${data.report_version?.version_number || ''}.`)
      if (data.signed_url) window.open(data.signed_url, '_blank', 'noopener,noreferrer')
      if (onChanged) onChanged()
    } catch (err) {
      setReportError(err.message || 'Could not save General report version.')
    } finally {
      setReportBusy('')
    }
  }

  function normaliseReportRecipient(value) {
    return String(value || '').trim().toLowerCase()
  }

  function isValidReportRecipient(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normaliseReportRecipient(value))
  }

  function generalReportEmailConfirmationToken(jobId, recipient) {
    return `general-report-email:${String(jobId || '').trim()}:${normaliseReportRecipient(recipient)}`
  }

  function clearGeneralReportEmailConfirmation() {
    setReportEmailConfirmationText('')
    setReportEmailConfirmedRecipient('')
  }

  function confirmationPhraseMatches(value) {
    return String(value || '') === GENERAL_REPORT_EMAIL_CONFIRMATION_PHRASE
  }

  function validateGeneralReportRecipient() {
    const to = normaliseReportRecipient(reportEmailTo)
    setReportNotice('')

    if (!to) {
      setReportEmailConfirmedRecipient('')
      setReportError('Add a customer email before sending the General report.')
      return false
    }
    if (!isValidReportRecipient(to)) {
      setReportEmailConfirmedRecipient('')
      setReportError('Enter a valid customer email before sending the General report.')
      return false
    }

    setReportError('')
    return true
  }

  async function emailGeneralReport() {
    if (outboundTestMode) {
      clearGeneralReportEmailConfirmation()
      setReportError('Outbound report email is disabled in test mode.')
      return
    }
    const to = normaliseReportRecipient(reportEmailTo)
    if (!to) {
      setReportEmailConfirmedRecipient('')
      setReportError('Add a customer email before sending the General report.')
      return
    }
    if (!isValidReportRecipient(to)) {
      setReportEmailConfirmedRecipient('')
      setReportError('Enter a valid customer email before sending the General report.')
      return
    }
    if (!confirmationPhraseMatches(reportEmailConfirmationText) || reportEmailConfirmedRecipient !== to) {
      setReportError(`Type ${GENERAL_REPORT_EMAIL_CONFIRMATION_PHRASE} to confirm sending this General report email.`)
      return
    }

    setReportBusy('send')
    setReportError('')
    setReportNotice('')
    setReportHistoryError('')
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) throw new Error('Please sign in again before sending a customer report.')

      const res = await fetch('/.netlify/functions/send-general-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          jobId: initialData.id,
          to,
          confirmSend: true,
          confirmedRecipient: to,
          confirmationToken: generalReportEmailConfirmationToken(initialData.id, to),
          confirmationPhrase: GENERAL_REPORT_EMAIL_CONFIRMATION_PHRASE,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Could not email General report.')
      if (data.report_version) mergeReportVersion(data.report_version)
      setReportSentAt(recordedReportSentAt(data))
      setReportEmailMode(false)
      setReportNotice(`Emailed General report to ${to}.`)
      if (data.warning) setReportHistoryError(data.warning)
      if (onChanged) onChanged()
    } catch (err) {
      setReportError(err.message || 'Could not email General report.')
    } finally {
      setReportBusy('')
    }
  }

  function renderReportVersions() {
    if (reportVersionsLoading) return <div className="fs12 text-muted">Loading saved General reports...</div>

    return (
      <>
        {reportHistoryError && <div className="alert alert-error" style={{ marginBottom: 10 }}>{reportHistoryError}</div>}
        {reportVersions.length ? (
          <div style={{ display: 'grid', gap: 8 }}>
            {reportVersions.map(version => (
              <div key={version.id} className="general-report-version-row">
                <StatusPill tone={version.delivery_status === 'sent' ? 'green' : 'blue'}>
                  V{version.version_number}
                </StatusPill>
                <div style={{ minWidth: 0 }}>
                  <div className="text-white fw7">
                    {reportDeliveryLabel(version.delivery_status)}
                    {version.recipient_email ? <span className="text-muted"> to {version.recipient_email}</span> : null}
                  </div>
                  <div className="fs11 text-muted" style={{ marginTop: 3 }}>
                    {fmtDateTime(reportDisplayTime(version))}
                    {Number(version.total_charged || 0) > 0 ? ` - ${formatServiceMoney(version.total_charged)}` : ''}
                    {version.file_name ? ` - ${version.file_name}` : ''}
                    <ReportDeliveryHistory version={version} formatDate={fmtDateTime} />
                  </div>
                </div>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => openGeneralReportVersion(version)}
                  disabled={reportBusy === `open-${version.id}`}
                >
                  {reportBusy === `open-${version.id}` ? 'Opening...' : 'Open PDF'}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="fs12 text-muted">No saved General report versions yet.</div>
        )}
      </>
    )
  }

  function renderGeneralReportActions() {
    const unresolvedChargeableStockCount = jobFinancials({ ...(initialData || {}), module_key: 'general' }).unresolvedChargeableStockCount
    const canReport = Boolean(initialData?.id) && !unresolvedChargeableStockCount
    const normalisedReportEmailTo = normaliseReportRecipient(reportEmailTo)
    const reportEmailReadyToSend = Boolean(
      normalisedReportEmailTo &&
      isValidReportRecipient(normalisedReportEmailTo) &&
      confirmationPhraseMatches(reportEmailConfirmationText) &&
      reportEmailConfirmedRecipient === normalisedReportEmailTo
    )
    const sendDisabled = Boolean(reportBusy) || !reportEmailReadyToSend || outboundTestMode

    return (
      <div className="job-modal-callout general-report-action-card" style={{ marginBottom: 14 }}>
        <div className="section-head compact" style={{ marginBottom: 10 }}>
          <div>
            <div className="job-modal-callout-title">Report &amp; Receipt</div>
            <div className="text-muted fs12">Preview, save or email the customer-facing General Workshop report.</div>
          </div>
          <StatusPill tone={reportVersions.length ? 'green' : 'muted'}>
            {reportVersions.length ? `${reportVersions.length} saved` : 'No saved PDFs'}
          </StatusPill>
        </div>

        {reportNotice && <div className="alert alert-success" style={{ marginBottom: 10 }}>{reportNotice}</div>}
        {reportError && <div className="alert alert-error" style={{ marginBottom: 10 }}>{reportError}</div>}
        {unresolvedChargeableStockCount > 0 && (
          <div className="alert alert-error" style={{ marginBottom: 10 }}>
            Resolve {unresolvedChargeableStockCount} chargeable draft stock item{unresolvedChargeableStockCount === 1 ? '' : 's'} before previewing, saving or emailing an itemised report.
          </div>
        )}

        <div className="action-row" style={{ marginTop: 12 }}>
          <button className="btn btn-green" onClick={previewGeneralReport} disabled={!canReport || Boolean(reportBusy)}>
            {reportBusy === 'preview' ? 'Preparing...' : 'Preview Report & Receipt'}
          </button>
          <button className="btn btn-ghost" onClick={saveGeneralReportVersion} disabled={!canReport || Boolean(reportBusy)}>
            {reportBusy === 'save' ? 'Saving...' : 'Save PDF Version'}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              setReportEmailMode(true)
              clearGeneralReportEmailConfirmation()
              setReportNotice('')
              setReportError(String(reportEmailTo || '').trim() ? '' : 'No customer email is saved. Add a recipient before sending.')
            }}
            disabled={!canReport || Boolean(reportBusy) || outboundTestMode}
            title={outboundTestMode ? 'Outbound email is disabled in test mode.' : undefined}
          >
            Email Report
          </button>
        </div>

        {outboundTestMode && <div className="alert alert-info" style={{ marginTop: 10 }}>Test mode: outbound report email is disabled.</div>}

        {reportEmailMode && (
          <div className="general-report-email-row">
            <input
              type="email"
              value={reportEmailTo}
              onChange={e => {
                setReportEmailTo(e.target.value)
                clearGeneralReportEmailConfirmation()
                const next = normaliseReportRecipient(e.target.value)
                if (!next) setReportError('Add a customer email before sending the General report.')
                else if (!isValidReportRecipient(next)) setReportError('Enter a valid customer email before sending the General report.')
                else setReportError('')
              }}
              placeholder="customer@email.com"
              autoFocus
            />
            <input
              type="text"
              value={reportEmailConfirmationText}
              onChange={e => {
                const value = e.target.value
                setReportEmailConfirmationText(value)
                if (!validateGeneralReportRecipient()) return
                if (confirmationPhraseMatches(value)) {
                  setReportEmailConfirmedRecipient(normalisedReportEmailTo)
                  setReportError('')
                } else {
                  setReportEmailConfirmedRecipient('')
                  if (value) setReportError(`Type ${GENERAL_REPORT_EMAIL_CONFIRMATION_PHRASE} exactly to enable sending.`)
                }
              }}
              placeholder={`Type ${GENERAL_REPORT_EMAIL_CONFIRMATION_PHRASE} to confirm`}
              aria-label="General report email confirmation phrase"
            />
            <button
              className="btn btn-primary btn-sm"
              onClick={emailGeneralReport}
              disabled={sendDisabled}
            >
              {reportBusy === 'send' ? 'Sending...' : 'Send Report Email'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setReportEmailMode(false)} disabled={reportBusy === 'send'}>Cancel</button>
          </div>
        )}

        <div style={{ marginTop: 14 }}>
          <div className="fs11 text-muted fw7" style={{ textTransform: 'uppercase', marginBottom: 8 }}>Saved versions</div>
          {renderReportVersions()}
        </div>
      </div>
    )
  }

  function renderCustomerReport({ editable = false, showTitle = true } = {}) {
    const completedText = editable ? form.general_work_completed : initialData?.general_work_completed
    const customerNotes = editable ? form.general_customer_notes : initialData?.general_customer_notes
    return (
      <>
        {showTitle && <div className="sec">Customer Report &amp; Handover</div>}
        <div className="job-modal-callout" style={{ marginBottom: 14 }}>
          <div className="section-head compact" style={{ marginBottom: 10 }}>
            <div>
              <div className="job-modal-callout-title">General Workshop customer report</div>
              <div className="text-muted fs12">Customer-facing report text. Selected customer-visible findings and evidence can also be included.</div>
            </div>
            <span className="job-status-chip job-status-blue">General only</span>
          </div>

          {editable ? (
            <>
              <div className="field">
                <label>Work completed for customer</label>
                <textarea
                  value={form.general_work_completed}
                  onChange={e => setField('general_work_completed', e.target.value)}
                  placeholder="Summarise the work completed in customer-friendly language."
                  style={{ minHeight: 90 }}
                  disabled={saving}
                />
              </div>
              <div className="field">
                <label>Customer notes / handover notes</label>
                <textarea
                  value={form.general_customer_notes}
                  onChange={e => setField('general_customer_notes', e.target.value)}
                  placeholder="Add customer-facing handover notes, care advice, or next steps."
                  style={{ minHeight: 90 }}
                  disabled={saving}
                />
              </div>
            </>
          ) : (
            <>
              <div className="detail-row">
                <span className="detail-label">Work completed</span>
                <span className="detail-val" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{completedText || 'No customer-facing work completed text yet.'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Customer notes</span>
                <span className="detail-val" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{customerNotes || 'No customer-facing handover notes yet.'}</span>
              </div>
              <div className="fs12 text-muted" style={{ marginTop: 8 }}>
                Internal notes, internal finding notes, finding estimates, internal evidence, prices, invoices and payment information are not included in General reports.
              </div>
            </>
          )}
        </div>
      </>
    )
  }

  function renderFindings({ editable = false, showTitle = true } = {}) {
    const canAddFindings = Boolean(initialData?.id)
    return (
      <>
        {showTitle && <div className="sec">Other Internal Findings</div>}
        <div className="job-modal-callout" style={{ marginBottom: 14 }}>
          <div className="section-head compact" style={{ marginBottom: 10 }}>
            <div>
              <div className="job-modal-callout-title">Other Internal Findings</div>
              <div className="text-muted fs12">Internal by default. Only findings explicitly marked for the customer report use customer-facing notes.</div>
            </div>
            <StatusPill tone={activeFindingCount ? 'orange' : 'muted'}>
              {activeFindingCount ? `${activeFindingCount} active` : 'No active findings'}
            </StatusPill>
          </div>

          {findingError && <div className="alert alert-error" style={{ marginBottom: 10 }}>{findingError}</div>}
          {findingsLoading && <div className="fs12 text-muted">Loading internal findings...</div>}

          {!findingsLoading && sortedFindings.length > 0 && (
            <div style={{ display: 'grid', gap: 8, marginBottom: editable ? 12 : 0 }}>
              {sortedFindings.map(finding => {
                const severity = findingSeverityMeta(finding.severity)
                const status = findingStatusMeta(finding.status)
                const estimatedCost = moneyNumber(finding.estimated_cost)
                return (
                  <div key={finding.id} className="muted-empty" style={{ borderStyle: 'solid', textAlign: 'left' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <div style={{ minWidth: 0, flex: '1 1 260px' }}>
                        <div className="action-row" style={{ gap: 6, justifyContent: 'flex-start', marginBottom: 6 }}>
                          <StatusPill tone={severity.tone}>{severity.label}</StatusPill>
                          <StatusPill tone={status.tone}>{status.label}</StatusPill>
                          {finding.customer_visible && <span className="job-status-chip job-status-green">Customer report</span>}
                          <span className="fs11 text-muted">{findingCategoryLabel(finding.category)}</span>
                        </div>
                        <div className="text-white fw7">{finding.title}</div>
                        {finding.notes && <div className="fs12 text-muted" style={{ marginTop: 5, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{finding.notes}</div>}
                        {finding.customer_visible && finding.customer_notes && (
                          <div className="fs12" style={{ marginTop: 6, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                            <span className="text-green fw7">Customer notes: </span>{finding.customer_notes}
                          </div>
                        )}
                        {estimatedCost > 0 && <div className="fs12 text-green fw7" style={{ marginTop: 6 }}>Internal estimate {formatServiceMoney(estimatedCost)}</div>}
                      </div>
                      {editable && (
                        <div className="action-row" style={{ gap: 6, alignItems: 'center' }}>
                          <select
                            value={status.key}
                            onChange={e => updateFindingStatus(finding, e.target.value)}
                            disabled={Boolean(findingSaving)}
                            style={{ minWidth: 130 }}
                          >
                            {FINDING_STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                          </select>
                          <button className="btn btn-ghost btn-sm" onClick={() => startEditFinding(finding)} disabled={Boolean(findingSaving)}>Edit</button>
                          <button className="btn btn-primary btn-sm" onClick={() => startAdditionalWorkFromFinding(finding)} disabled={Boolean(findingSaving)}>Additional Work</button>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {!findingsLoading && !sortedFindings.length && (
            <div className="fs12 text-muted" style={{ marginBottom: editable ? 12 : 0 }}>No internal findings recorded yet.</div>
          )}

          {editable && !canAddFindings && (
            <div className="muted-empty">Save the General job before adding internal findings.</div>
          )}

          {editable && canAddFindings && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 12, background: 'var(--black)' }}>
              <div className="fs11 text-muted fw7" style={{ textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>
                {editingFindingId ? 'Edit finding' : 'Add finding'}
              </div>
              <datalist id="general-finding-category-options">
                {FINDING_CATEGORY_OPTIONS.map(([value, label]) => <option key={value} value={label} />)}
              </datalist>
              <div className="grid-3">
                <div className="field">
                  <label>Category</label>
                  <input
                    list="general-finding-category-options"
                    value={findingForm.category}
                    onChange={e => setFindingField('category', e.target.value)}
                    placeholder="Brakes, drivetrain, wheels..."
                    disabled={Boolean(findingSaving)}
                  />
                </div>
                <div className="field">
                  <label>Severity</label>
                  <select value={findingForm.severity} onChange={e => setFindingField('severity', e.target.value)} disabled={Boolean(findingSaving)}>
                    {FINDING_SEVERITY_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>Status</label>
                  <select value={findingForm.status} onChange={e => setFindingField('status', e.target.value)} disabled={Boolean(findingSaving)}>
                    {FINDING_STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid-2">
                <div className="field">
                  <label>Title</label>
                  <input
                    value={findingForm.title}
                    onChange={e => setFindingField('title', e.target.value)}
                    placeholder="e.g. Rear brake pads worn below safe limit"
                    disabled={Boolean(findingSaving)}
                  />
                </div>
                <div className="field">
                  <label>Internal estimated cost</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={findingForm.estimated_cost}
                    onChange={e => setFindingField('estimated_cost', e.target.value)}
                    placeholder="0.00"
                    disabled={Boolean(findingSaving)}
                  />
                  <div className="field-hint">Internal only. Does not affect job totals, quotes, invoices or payments.</div>
                </div>
              </div>
              <div className="field">
                <label>Internal Notes</label>
                <textarea
                  value={findingForm.notes}
                  onChange={e => setFindingField('notes', e.target.value)}
                  placeholder="Workshop notes about what was found, checked, or recommended."
                  style={{ minHeight: 72 }}
                  disabled={Boolean(findingSaving)}
                />
              </div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  margin: '0 0 10px',
                  textTransform: 'none',
                  letterSpacing: 0,
                  color: 'var(--text)',
                }}
              >
                <input
                  type="checkbox"
                  checked={Boolean(findingForm.customer_visible)}
                  onChange={e => setFindingField('customer_visible', e.target.checked)}
                  disabled={Boolean(findingSaving)}
                  style={{ width: 'auto', marginTop: 2 }}
                />
                <span>
                  <span className="fw7">Include in customer report</span>
                  <span className="fs12 text-muted" style={{ display: 'block', marginTop: 2 }}>
                    Uses customer-facing notes only. Internal notes and estimated cost stay private.
                  </span>
                </span>
              </label>
              {findingForm.customer_visible && (
                <div className="field">
                  <label>Customer-facing notes</label>
                  <textarea
                    value={findingForm.customer_notes}
                    onChange={e => setFindingField('customer_notes', e.target.value)}
                    placeholder="Explain the finding in customer-friendly language."
                    style={{ minHeight: 72 }}
                    disabled={Boolean(findingSaving)}
                  />
                  <div className="field-hint">Required when this finding is included in the customer report.</div>
                </div>
              )}
              <div className="action-row">
                <button className="btn btn-primary btn-sm" onClick={saveFinding} disabled={Boolean(findingSaving)}>
                  {findingSaving ? 'Saving...' : editingFindingId ? 'Save Finding' : 'Add Finding'}
                </button>
                {editingFindingId && <button className="btn btn-ghost btn-sm" onClick={cancelFindingEdit} disabled={Boolean(findingSaving)}>Cancel Edit</button>}
              </div>
            </div>
          )}
        </div>
      </>
    )
  }

  function renderEvidence({ editable = false, showTitle = true } = {}) {
    const canAddEvidence = Boolean(initialData?.id)
    return (
      <>
        {showTitle && <div className="sec">General Evidence</div>}
        <div className="job-modal-callout" style={{ marginBottom: 14 }}>
          <div className="section-head compact" style={{ marginBottom: 10 }}>
            <div>
              <div className="job-modal-callout-title">General job-level evidence</div>
              <div className="text-muted fs12">Uploads are internal by default. Add checklist or additional-work evidence inside those sections when it relates to a specific item.</div>
            </div>
            <span className="job-status-chip job-status-blue">General only</span>
          </div>
          {canAddEvidence ? (
            <JobEvidence
              jobId={initialData.id}
              workshopId={effectiveWorkshopId}
              mode={editable ? 'edit' : 'view'}
              context="general"
              onChanged={handleNestedChanged}
            />
          ) : (
            <div className="fs12 text-muted">Save the General job before adding photos, videos or files.</div>
          )}
        </div>
      </>
    )
  }

  function renderStockUsage({ editable = false } = {}) {
    return (
      <>
      <JobStockUsagePanel
        vatInherited={inheritedServicePrices}
        jobId={initialData?.id}
        workshopId={effectiveWorkshopId}
        mode={editable ? 'edit' : 'view'}
        sourceType="general_job"
        title="General stock usage"
        description="Job-level stock and consumables are recorded as used immediately. Chargeable rows count as billable parts/materials on this General job."
        onChanged={handleStockUsageChanged}
      />
      </>
    )
  }

  function renderChecklist({ editable = false } = {}) {
    const checklistWorkshopId = effectiveWorkshopId
    const canOverrideQc = canAuthoriseChecklistOverride(profile, checklistWorkshopId)
    return (
      <GeneralChecklistPanel
        jobId={initialData?.id}
        workshopId={checklistWorkshopId}
        mode={editable ? 'edit' : 'view'}
        canOverrideQc={canOverrideQc}
        onCreateAdditionalWork={startAdditionalWorkFromChecklist}
        onCreateExtraServiceLine={startExtraServiceLineFromChecklist}
        onChanged={handleNestedChanged}
        onSummary={setChecklistSummary}
      />
    )
  }

  function renderExtraServiceLines({ editable = false } = {}) {
    return (
      <GeneralExtraServiceLinePanel
        job={initialData}
        workshopId={effectiveWorkshopId}
        services={allGeneralServices}
        mode={editable ? 'edit' : 'view'}
        refreshKey={evidenceRefreshKey}
        customerEmail={selectedCustomer?.email || ''}
        sourceDraft={extraServiceLineSource}
        onSourceConsumed={() => setExtraServiceLineSource(null)}
        onTotalsChange={setExtraServiceLineBillable}
        onRowsChange={handleExtraServiceLineRowsChange}
        onChanged={handleNestedChanged}
      />
    )
  }

  function renderAdditionalWork({ editable = false } = {}) {
    return (
      <GeneralAdditionalWorkPanel
        job={initialData}
        workshopId={effectiveWorkshopId}
        services={allGeneralServices}
        mode={editable ? 'edit' : 'view'}
        refreshKey={evidenceRefreshKey}
        customerEmail={selectedCustomer?.email || ''}
        sourceDraft={additionalWorkSource}
        onSourceConsumed={() => setAdditionalWorkSource(null)}
        onTotalsChange={setAdditionalWorkBillable}
        onChanged={handleNestedChanged}
      />
    )
  }

  function renderDiscounts() {
    if (inheritedServicePrices) return null
    return <GeneralJobDiscounts key={`${effectiveWorkshopId}/${initialData?.id || 'new'}`} jobId={initialData?.id} workshopId={effectiveWorkshopId} profile={profile}
      dirty={!readOnly && (JSON.stringify(form) !== JSON.stringify(savedForm || originalForm.current) || (isEdit && serviceBaseAmount !== Number(discountContext?.job?.labour_charge ?? initialData?.labour_charge ?? 0)))} disabled={saving}
      refreshKey={`${initialData?.financial_revision_key || ''}/${stockBillable}/${additionalWorkBillable}/${extraServiceLineBillable}`}
      onApplied={() => { if (onChanged) onChanged() }}
      onContext={data => {
        setDiscountContext(data)
        setPaymentPatch(current => ({ ...current, ...data.job, job_price_adjustments: data.adjustments }))
      }} />
  }

  function renderPaymentPanel({ amount, pricingModeValue, priceStatusValue, allowActions = false } = {}) {
    const amountValue = moneyNumber(amount)
    const actionJob = {
      ...paymentJob,
      pricing_mode: pricingModeValue || pricingMode,
      price_status: priceStatusValue || priceStatus,
    }
    const paymentState = jobPaymentActionState(actionJob, {
      ...jobFinancials(actionJob),
      totalCost: amountValue,
    })
    const payment = paymentState.payment
    const activePaymentLink = allowActions ? paymentState.activePaymentLink : ''
    const canCreateLink = allowActions && paymentState.canCreateLink && !outboundTestMode
    const canCopyLink = allowActions && paymentState.canCopyLink
    const canSendReminder = allowActions && paymentState.canSendReminder && !outboundTestMode
    const manualPaymentLifecycleBlocked = ['completed', 'closed', 'collected', 'cancelled', 'voided']
      .includes(normaliseJobStatus(actionJob.status))
    const canRecordManualPayment = allowActions
      && canMarkPaidManually
      && payment.key !== 'requires_review'
      && jobPriceStatus(actionJob) === 'final'
      && !manualPaymentLifecycleBlocked
      && amountValue > 0
    const firstSend = !paymentJob.payment_reminder_sent_at
    const sendLabel = firstSend ? 'Email Payment Request & Report' : 'Send Reminder'
    const savedJobNotice = !initialData?.id
      ? 'Save the General job before creating a payment link.'
      : !allowActions
        ? 'Save and view the General job before taking payment actions.'
        : ''

    return (
      <div className="general-payment-panel">
        <div className="job-modal-callout" style={{ marginBottom: 12 }}>
          <div>
            <div className="job-modal-callout-title">Stripe Standard payment</div>
            <div className="text-muted fs12">General payment links are available only when pricing is final and the job has a positive total.</div>
          </div>
          <StatusPill tone={payment.tone}>Payment: {payment.label}</StatusPill>
        </div>

        {paymentNotice && <div className="alert alert-success" style={{ marginBottom: 10 }}>{paymentNotice}</div>}
        {paymentError && <div className="alert alert-error" style={{ marginBottom: 10 }}>{paymentError}</div>}

        <div className="detail-row"><span className="detail-label">Payable total</span><span className="detail-val">{pricingDisplay({ pricingMode: pricingModeValue || pricingMode, priceStatus: priceStatusValue || priceStatus, amount: amountValue })}</span></div>
        {paymentJob.payment_paid_at && <div className="detail-row"><span className="detail-label">Payment paid</span><span className="detail-val">{fmtDateTime(paymentJob.payment_paid_at)}</span></div>}
        {paymentJob.payment_method && <div className="detail-row"><span className="detail-label">Payment method</span><span className="detail-val">{labelText(paymentJob.payment_method)}</span></div>}
        {paymentJob.payment_reference && <div className="detail-row"><span className="detail-label">Payment reference</span><span className="detail-val">{paymentJob.payment_reference}</span></div>}
        {paymentJob.payment_requested_at && payment.key !== 'paid' && <div className="detail-row"><span className="detail-label">Requested</span><span className="detail-val">{fmtDateTime(paymentJob.payment_requested_at)}</span></div>}
        {paymentJob.payment_reminder_sent_at && payment.key !== 'paid' && <div className="detail-row"><span className="detail-label">Last sent</span><span className="detail-val">{fmtDateTime(paymentJob.payment_reminder_sent_at)}</span></div>}
        {activePaymentLink && !paymentJob.payment_reminder_sent_at && payment.key !== 'paid' && (
          <div className="alert alert-info" style={{ marginTop: 10 }}>Payment link ready — customer not contacted.</div>
        )}

        {paymentState.refreshReason && payment.key !== 'paid' && (
          <div className="alert alert-info" style={{ marginTop: 10 }}>{paymentState.refreshReason}</div>
        )}
        {paymentState.blockedReason && payment.key !== 'paid' && (
          <div className="alert alert-info" style={{ marginTop: 10 }}>{paymentState.blockedReason}</div>
        )}
        {savedJobNotice && payment.key !== 'paid' && (
          <div className="alert alert-info" style={{ marginTop: 10 }}>{savedJobNotice}</div>
        )}
        {outboundTestMode && payment.key !== 'paid' && (
          <div className="alert alert-info" style={{ marginTop: 10 }}>Test mode: payment creation and outbound payment messages are disabled.</div>
        )}

        {allowActions && payment.key !== 'paid' && (
          <div className="action-row" style={{ marginTop: 12 }}>
            <PaymentRequestEmail job={paymentJob} customer={selectedCustomer} canCreate={canCreateLink} canSend={canSendReminder} disabled={Boolean(paymentBusy) || outboundTestMode}
              onBusy={value => { setPaymentBusy(value ? 'payment-only' : ''); if (value) { setPaymentNotice(''); setPaymentError('') } }}
              onResult={({ generated, sent }) => {
                setPaymentPatch(current => ({ ...current,
                  ...(generated ? { payment_link: generated.url, payment_status: generated.payment_status || 'requested', stripe_checkout_session_id: generated.checkout_session_id, payment_refresh_required: false, payment_refresh_reason: '' } : {}),
                  ...(sent ? { payment_reminder_sent_at: sent.payment_reminder_sent_at } : {}),
                }))
                if (sent) setPaymentNotice('Payment request emailed. No report or photos attached.')
                if (onChanged) onChanged()
              }} />
            {canCreateLink && (
              <button className="btn btn-primary btn-sm" onClick={createGeneralPaymentLink} disabled={Boolean(paymentBusy)}>
                {paymentBusy === 'create' ? 'Generating...' : 'Generate Link Only'}
              </button>
            )}
            {canCopyLink && (
              <button className="btn btn-ghost btn-sm" onClick={() => copyGeneralPaymentLink(activePaymentLink)} disabled={Boolean(paymentBusy)}>
                Copy Link
              </button>
            )}
            {canSendReminder && (
              <button className="btn btn-green btn-sm" onClick={sendGeneralPaymentReminder} disabled={Boolean(paymentBusy) || !selectedCustomer?.email}>
                {paymentBusy === 'reminder' ? 'Sending...' : sendLabel}
              </button>
            )}
            {canRecordManualPayment && (
              <button className="btn btn-ghost btn-sm" onClick={() => markGeneralPaidManually(amountValue)} disabled={Boolean(paymentBusy)}>
                {paymentBusy === 'paid' ? 'Saving...' : 'Mark Paid Manually'}
              </button>
            )}
          </div>
        )}
      </div>
    )
  }

  if (readOnly) {
    const viewAmount = discountContext ? Number(discountContext.payable.payable_amount_pence) / 100 : approvedDiscount > 0 ? internalJobTotal : Array.isArray(initialData?.job_additional_work_requests) || Array.isArray(initialData?.job_extra_service_line_requests)
      ? internalJobTotal
      : moneyNumber(Math.max(internalJobTotal, moneyNumber(initialData?.total_charged), moneyNumber(initialData?.labour_charge)))
    const viewPricingMode = jobPricingMode(paymentJob)
    const viewPriceStatus = jobPriceStatus(paymentJob)
    const viewPriceLabel = pricingDisplay({ pricingMode: viewPricingMode, priceStatus: viewPriceStatus, amount: viewAmount })
    const viewPlannedMinutes = plannedMinutesForJob(initialData || {}, { services: sourceServices })
    const viewPayment = jobPaymentActionState({
      ...paymentJob,
      pricing_mode: viewPricingMode,
      price_status: viewPriceStatus,
    }, {
      ...jobFinancials({ ...(initialData || {}), module_key: 'general' }),
      totalCost: viewAmount,
    }).payment
    const viewReportStatus = effectiveReportSentAt
      ? `Sent ${fmtDateTime(effectiveReportSentAt)}`
      : reportVersions.length
        ? `${reportVersions.length} saved PDF${reportVersions.length === 1 ? '' : 's'}`
        : 'Not sent'
    const viewBikeLabel = selectedBike
      ? bikeName(selectedBike)
      : compactText(initialData?.bike_context || initialData?.bike || '', 'No bike linked')

    return (
      <div className="modal-overlay">
        <div className="modal modal-wide job-workspace-modal suspension-workspace general-workspace read-only-job-workspace">
          <header className="suspension-workspace-header general-workspace-header">
            <div className="suspension-workspace-topline">
              <button type="button" className="suspension-workspace-back" onClick={onClose}>← {returnLabel}</button>
              <div className="suspension-workspace-actions">
                  {initialData?.id && <a className="btn btn-ghost btn-sm" href={`/jobs/${initialData.id}/time`} target="_blank" rel="noreferrer" aria-label="Actual time, opens in a new tab">Actual time ↗</a>}
                <StatusPill tone={currentStatus.tone}>{currentStatus.label}</StatusPill>
                <StatusPill tone={viewPayment.tone}>Payment: {viewPayment.label}</StatusPill>
                {paymentJob.administrative_closed_at && <StatusPill tone="orange">Administrative close</StatusPill>}
                <button className="close-btn" onClick={onClose}>x</button>
              </div>
            </div>
            <div className="suspension-workspace-title-row">
              <div>
                <div className="refresh-kicker">Read-only General job · {initialData?.job_number || 'General'}</div>
                <div className="modal-title">{viewBikeLabel}</div>
                <div className="text-muted fs12">{customerName(selectedCustomer)} · {overviewServices}</div>
              </div>
              <div className="read-only-job-price"><span>Recorded total</span><strong>{viewPriceLabel}</strong></div>
            </div>
          </header>

          <div className="sec" style={{ marginTop: 0 }}>Job Details</div>
          <div className="detail-row"><span className="detail-label">Job Number</span><span className="detail-val">{initialData?.job_number || 'General job'}</span></div>
          <div className="detail-row"><span className="detail-label">Invoice Number</span><span className="detail-val">{initialData?.invoice_number || 'Not issued'}</span></div>
          <div className="detail-row"><span className="detail-label">Customer</span><span className="detail-val">{customerName(selectedCustomer)}</span></div>
          <div className="detail-row"><span className="detail-label">Status</span><span className="detail-val"><StatusPill tone={currentStatus.tone}>{currentStatus.label}</StatusPill></span></div>
          <div className="detail-row"><span className="detail-label">Bike</span><span className="detail-val">{viewBikeLabel}</span></div>
          <div className="detail-row"><span className="detail-label">Received Date</span><span className="detail-val">{initialData?.date ? fmtDate(initialData.date) : 'Not recorded'}</span></div>
          <div className="detail-row"><span className="detail-label">Date Booked In For</span><span className="detail-val">{initialData?.scheduled_date ? fmtDate(initialData.scheduled_date) : 'Not scheduled'}</span></div>
          <div className="detail-row"><span className="detail-label">Date For Collection / Returned</span><span className="detail-val">{initialData?.due_date ? fmtDate(initialData.due_date) : 'Not set'}</span></div>
          <div className="detail-row"><span className="detail-label">Bench / Location</span><span className="detail-val">{initialData?.physical_location || 'Not set'}</span></div>
          <div className="detail-row"><span className="detail-label">Selected Services</span><span className="detail-val">{overviewServices}</span></div>
          <div className="detail-row"><span className="detail-label">Technician</span><span className="detail-val">{selectedTechnician?.name || initialData?.technician || 'Unassigned'}</span></div>
          <div className="detail-row"><span className="detail-label">Planned Time</span><span className="detail-val">{viewPlannedMinutes ? formatPlannedMinutes(viewPlannedMinutes) : 'Time required'}</span></div>
          <div className="detail-row"><span className="detail-label">Price</span><span className="detail-val">{viewPriceLabel}</span></div>
          <div className="detail-row"><span className="detail-label">Payment Status</span><span className="detail-val">{viewPayment.label}</span></div>
          <div className="detail-row"><span className="detail-label">Report Status</span><span className="detail-val">{viewReportStatus}</span></div>
          <div className="detail-row">
            <span className="detail-label">Progress</span>
            <span className="detail-val">
              <span className="action-row">
                <StatusPill tone={checklistSummary.blockers?.length ? 'orange' : 'green'}>Checklist: {overviewChecklist}</StatusPill>
                <StatusPill tone={reportHasContent ? 'green' : 'muted'}>Report: {overviewReport}</StatusPill>
              </span>
            </span>
          </div>
          {onEdit && (
            <div className="action-row" style={{ margin: '8px 0 16px' }}>
              <button className="btn btn-primary btn-sm" onClick={onEdit}>Schedule / Assign</button>
            </div>
          )}

          <div className="sec">Work</div>
          {displayServices.length > 0 && (
            <div style={{ display: 'grid', gap: 8, marginBottom: 10 }}>
              {displayServices.map((service, index) => (
                <div key={service.id || index} className="muted-empty" style={{ borderStyle: 'solid' }}>
                  <div className="text-white fw7">{service.name}</div>
                  <div className="fs12 text-muted">{serviceCategoryLabel(service)} - {serviceDisplayPrice(service)}</div>
                  {serviceBookingType(service) !== 'assessment_first' && serviceBookingLabel(service) && <div className="fs11 text-muted" style={{ marginTop: 4 }}>{serviceBookingLabel(service)}</div>}
                </div>
              ))}
            </div>
          )}
          {renderApprovedExtraServiceScope()}
          {jobServiceSummary(initialData || {}) && <div className="detail-val" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{jobServiceSummary(initialData || {})}</div>}

          {renderChecklist({ editable: false })}

          {renderExtraServiceLines({ editable: false })}

          {renderAdditionalWork({ editable: false })}

          {renderGeneralReportActions()}

          <GeneralJobFoldout
            title="Customer Report & Handover"
            subtitle="Customer-facing work completed notes and handover text."
            badge={reportHasContent ? 'Notes ready' : 'Draft empty'}
            badgeTone={reportHasContent ? 'green' : 'muted'}
            forceOpen={Boolean(reportNotice || reportError)}
          >
            {renderCustomerReport({ editable: false, showTitle: false })}
          </GeneralJobFoldout>

          <GeneralJobFoldout
            title="Other Internal Findings"
            subtitle="Workshop-only observations that are not part of the checklist."
            badge={activeFindingCount ? `${activeFindingCount} active` : 'No active findings'}
            badgeTone={activeFindingCount ? 'orange' : 'muted'}
            forceOpen={Boolean(findingError)}
          >
            {renderFindings({ editable: false, showTitle: false })}
          </GeneralJobFoldout>

          <GeneralJobFoldout
            title="General Evidence"
            subtitle="Job-level photos and files; item evidence stays with checklist or additional work."
            badge="Optional"
          >
            {renderEvidence({ editable: false, showTitle: false })}
          </GeneralJobFoldout>

          <GeneralJobFoldout
            title="Stock / Materials"
            subtitle="Optional job-level stock usage; additional-work stock stays with its request."
            badge="Optional"
          >
            {renderStockUsage({ editable: false })}
          </GeneralJobFoldout>

          <GeneralJobFoldout
            title="Pricing & Job Admin"
            subtitle="Pricing status, payment actions and private notes."
            badge={viewPriceLabel}
            badgeTone={viewPriceStatus === 'final' ? 'green' : viewPriceStatus === 'quote_required' ? 'orange' : 'blue'}
          >
            <div className="general-job-admin-panel">
            {renderPriceRepair()}
            <VatIntakeTotals intake={vatIntake} />
              {renderPaymentPanel({
                amount: viewAmount,
                pricingModeValue: viewPricingMode,
                priceStatusValue: viewPriceStatus,
                allowActions: true,
              })}
              {renderDiscounts()}
              <AdministrativeClosePanel
                profile={profile}
                job={paymentJob}
                missingStages={generalAdministrativeCloseMissing(paymentJob, checklistSummary)}
                onClosed={nextJob => {
                  setPaymentPatch(current => ({ ...current, ...nextJob }))
                  setPaymentNotice('Job administratively closed. Incomplete stages remain recorded.')
                  if (onChanged) onChanged()
                }}
              />
              <div className="detail-row"><span className="detail-label">Location</span><span className="detail-val">{initialData?.physical_location || 'Not set'}</span></div>
              <div className="detail-row"><span className="detail-label">Pricing</span><span className="detail-val">{labelText(viewPricingMode)} / {labelText(viewPriceStatus)}</span></div>
              {initialData?.notes && <div className="detail-row"><span className="detail-label">Internal notes</span><span className="detail-val" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{initialData.notes}</span></div>}
              {initialData?.further_work && <div className="detail-row"><span className="detail-label">Handover / Follow-up</span><span className="detail-val" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{initialData.further_work}</span></div>}
            </div>
          </GeneralJobFoldout>

          {renderInvoiceRecords()}
          <div className="action-row" style={{ marginTop: 16 }}>
            {onEdit && <button className="btn btn-primary" onClick={onEdit}>Edit Job</button>}
            <button className="btn btn-ghost" onClick={onClose}>{returnLabel}</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="modal-overlay">
      <div className={`modal ${isEdit ? 'job-workspace-modal suspension-workspace general-workspace' : 'modal-wide quick-intake-modal general-intake-modal'}`}>
        {isEdit ? (
          <>
            <header className="suspension-workspace-header general-workspace-header">
              <div className="suspension-workspace-topline">
                <button type="button" className="suspension-workspace-back" onClick={onClose}>← {returnLabel}</button>
                <div className="suspension-workspace-actions">
                  {initialData?.id && <a className="btn btn-ghost btn-sm" href={`/jobs/${initialData.id}/time`} target="_blank" rel="noreferrer" aria-label="Actual time, opens in a new tab">Actual time ↗</a>}
                  <StatusPill tone={currentStatus.tone}>{currentStatus.label}</StatusPill>
                  <StatusPill tone={workspacePayment.tone}>Payment: {workspacePayment.label}</StatusPill>
                  <span role="status" className="job-save-status">{!saving && !error && savedForm === form ? 'Changes saved.' : ''}</span>
                  <button type="button" className="btn btn-primary" onClick={save} disabled={saving || vatIntake.blocked}>
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                  <button type="button" className="close-btn" aria-label="Close job" onClick={onClose}>×</button>
                </div>
              </div>
              <div className="suspension-workspace-title-row">
                <div>
                  <div className="suspension-workspace-kicker">General job {initialData?.job_number ? `· ${initialData.job_number}` : ''}</div>
                  <h2>{overviewBike}</h2>
                  {process.env.REACT_APP_VAT_BUILD2 === 'true' && profile?.role === 'owner' && <a href={`/vat-documents/${initialData.id}`}>Prepare financial document</a>}
                  <p>{overviewCustomer} · {overviewServices}</p>
                </div>
                <div className="suspension-workspace-next">
                  <span>Next action</span>
                  <strong>{workspaceNextAction.title}</strong>
                  <small>{workspaceNextAction.detail}</small>
                </div>
              </div>
              <div className="suspension-workspace-progress" aria-label="General job progress">
                <StatusPill tone={checklistSummary.blockers?.length ? 'orange' : 'green'}>Checklist: {overviewChecklist}</StatusPill>
                <StatusPill tone={priceStatus === 'final' ? 'green' : 'blue'}>Pricing: {priceLabel}</StatusPill>
                <StatusPill tone={reportHasContent ? 'green' : 'muted'}>Report: {overviewReport}</StatusPill>
              </div>
            </header>
            <nav className="suspension-workspace-tabs general-workspace-tabs" aria-label="General job sections">
              {GENERAL_WORKSPACE_TABS.map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={workspaceTab === key ? 'is-active' : ''}
                  aria-current={workspaceTab === key ? 'page' : undefined}
                  onClick={() => setWorkspaceTab(key)}
                >
                  {label}
                </button>
              ))}
            </nav>
          </>
        ) : <div className="modal-header quick-intake-header">
          <div>
            <div className="refresh-kicker">Quick intake · General</div>
            <div className="modal-title">New General job</div>
          </div>
          <button ref={newJobCloseButtonRef} className="close-btn" aria-label="Close General intake" onClick={onClose}>x</button>
        </div>}

        {!isEdit && !canCreateGeneralJob && (
          <div className="alert alert-info">General Workshop job creation is disabled for this workshop.</div>
        )}
        {error && <div className="alert alert-error suspension-workspace-error" role="alert"><strong>Save failed.</strong> {error}</div>}

        {!isEdit && renderJobOverview()}

        <section className="general-workspace-panel" hidden={isEdit && workspaceTab !== 'work'}>
        <div className="sec" style={{ marginTop: 0 }}>Job Details</div>
        <div className="grid-2">
          <div className="field">
            <CustomerCombobox workshopId={effectiveWorkshopId} value={form.customer_id} selectedCustomer={selectedCustomer}
              onChange={(id, row) => { setChosenCustomer(row); changeCustomer(id) }} disabled={saving || (!isEdit && !canCreateGeneralJob)} />
          </div>
          <div className="field">
            <label>Bike</label>
            <select value={form.bike_id} onChange={e => setField('bike_id', e.target.value)} disabled={saving || !form.customer_id}>
              <option value="">{form.customer_id && bikeOptions.length ? 'Choose bike...' : 'No bike linked'}</option>
              {bikeOptions.map(row => <option key={row.id} value={row.id}>{bikeName(row)}</option>)}
            </select>
          </div>
        </div>

        <div className="grid-3">
          <div className="field">
            <label>Received Date</label>
            <input type="date" value={form.date} onChange={e => setField('date', e.target.value)} disabled={saving} />
          </div>
          <div className="field">
            <label>Date Booked In For</label>
            <input type="date" value={form.scheduled_date || ''} onChange={e => setField('scheduled_date', e.target.value)} disabled={saving} />
          </div>
          <div className="field">
            <label>Technician</label>
            <select value={selectedTechnician?.id || ''} onChange={e => setTechnicianAssignment(e.target.value)} disabled={saving}>
              <option value="">Unassigned</option>
              {teamMembers.map(member => (
                <option key={member.id} value={member.id}>
                  {member.name}{member.role ? ` - ${member.role}` : ''}
                </option>
              ))}
            </select>
            {form.technician && !selectedTechnician && <div className="field-hint">Legacy technician name: {form.technician}</div>}
          </div>
          <div className="field">
            <label>Planned Time</label>
            <div className="detail-val" style={{ minHeight: 38, display: 'flex', alignItems: 'center' }}>
              {plannedScheduleMinutes ? formatPlannedMinutes(plannedScheduleMinutes) : 'Time required'}
            </div>
            <div className="field-hint">From selected service settings.</div>
          </div>
        </div>
        {canOverrideSchedule && (
          <label className="check-item" style={{ marginBottom: 16 }}>
            <input
              type="checkbox"
              checked={scheduleOverrideOverCapacity}
              onChange={e => setScheduleOverrideOverCapacity(e.target.checked)}
              disabled={saving}
            />
            Allow over-capacity schedule save
          </label>
        )}

        <div className="grid-3">
          <div className="field">
            <label>Status</label>
            <select value={form.status} onChange={e => setField('status', e.target.value)} disabled={saving}>
              {lifecycleStatusOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Date For Collection / Returned</label>
            <input type="date" value={form.due_date || ''} onChange={e => setField('due_date', e.target.value)} disabled={saving} />
          </div>
          <div className="field">
            <label>Physical Location</label>
            <input value={form.physical_location} onChange={e => setField('physical_location', e.target.value)} placeholder="Bench, bay, rack, shelf..." disabled={saving} />
          </div>
        </div>

        <div className="sec">General Services</div>
        {selectableServices.length ? (
          <div className="general-service-picker">
            <div className="general-service-selected-panel">
              <div className="general-service-selected-head">
                <span>{selectedServiceRows.length ? `${selectedServiceRows.length} selected` : 'No services selected'}</span>
                {selectedServiceRows.length > 0 && <strong>{serviceSubtotalLabel}</strong>}
              </div>
              {selectedServiceRows.length > 0 ? (
                <div className="general-service-selected-list">
                  {selectedServiceRows.map(service => (
                    <button
                      type="button"
                      key={service.id}
                      className="general-service-selected-chip"
                      onClick={() => toggleService(String(service.id))}
                      disabled={saving}
                    >
                      <span>{service.name}</span>
                      <em>{serviceDisplayPrice(service)}</em>
                      <b aria-hidden="true">x</b>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="fs12 text-muted">Search or open a category to add one or more services.</div>
              )}
              {renderApprovedExtraServiceScope()}
            </div>

            <VatIntakeTotals intake={vatIntake} />
            <div className="general-service-picker-tools">
              <input
                type="search"
                value={serviceSearch}
                onChange={e => setServiceSearch(e.target.value)}
                placeholder="Search General services..."
                aria-label="Search General services"
                disabled={saving}
              />
              {serviceCategoryRows.length > 1 && (
                <div className="booking-service-filters general-service-category-nav">
                  {serviceCategoryRows.map(group => (
                    <button
                      type="button"
                      key={group.label}
                      className={expandedCategorySet.has(group.label) ? 'active' : ''}
                      onClick={() => toggleServiceCategory(group.label)}
                      disabled={saving}
                    >
                      {group.label}
                      <span>{group.selectedCount ? `${group.selectedCount}/${group.services.length}` : group.services.length}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="general-service-picker-results">
              {visibleServiceCategoryRows.length > 0 ? visibleServiceCategoryRows.map(group => {
                const open = serviceSearchTerm || expandedCategorySet.has(group.label) || serviceCategoryRows.length === 1
                return (
                  <div key={group.label} className="general-service-picker-group">
                    <button
                      type="button"
                      className="general-service-picker-group-head"
                      onClick={() => toggleServiceCategory(group.label)}
                      aria-expanded={Boolean(open)}
                      disabled={saving}
                    >
                      <span>{group.label}</span>
                      <em>{group.selectedCount ? `${group.selectedCount} selected` : `${group.services.length} service${group.services.length === 1 ? '' : 's'}`}</em>
                    </button>
                    {open && (
                      <div className="general-service-picker-list">
                        {group.services.map(renderServicePickerRow)}
                      </div>
                    )}
                  </div>
                )
              }) : (
                <div className="booking-service-empty">No General services match the current search.</div>
              )}
            </div>

            {serviceSearchTerm && (
              <div className="booking-service-count">{visibleServiceCount} matching service{visibleServiceCount === 1 ? '' : 's'}</div>
            )}
          </div>
        ) : (
          <div className="muted-empty" style={{ marginBottom: 14 }}>No active General services are configured yet. Enter requested work below.</div>
        )}

        <div className="field">
          <label>Service Summary / Requested Work</label>
          <textarea
            value={form.service_summary}
            onChange={e => setField('service_summary', e.target.value)}
            placeholder="Describe the requested work if no service is selected."
            style={{ minHeight: 92 }}
            disabled={saving}
          />
        </div>
        </section>

        <section className="general-workspace-panel" hidden={isEdit && workspaceTab !== 'checklist'}>
        {renderChecklist({ editable: true })}
        </section>

        <section className="general-workspace-panel" hidden={isEdit && workspaceTab !== 'approvals'}>
        {renderExtraServiceLines({ editable: true })}

        {renderAdditionalWork({ editable: true })}
        </section>

        <section className="general-workspace-panel" hidden={isEdit && workspaceTab !== 'admin'}>
        <GeneralJobFoldout
          title="Customer Report & Handover"
          subtitle="Customer-facing work completed notes and handover text."
          badge={reportHasContent ? 'Notes ready' : 'Draft empty'}
          badgeTone={reportHasContent ? 'green' : 'muted'}
          defaultOpen={false}
        >
          {renderCustomerReport({ editable: true, showTitle: false })}
        </GeneralJobFoldout>
        </section>

        <section className="general-workspace-panel" hidden={isEdit && workspaceTab !== 'approvals'}>
        <GeneralJobFoldout
          title="Other Internal Findings"
          subtitle="Workshop-only observations that are not part of the checklist."
          badge={activeFindingCount ? `${activeFindingCount} active` : 'No active findings'}
          badgeTone={activeFindingCount ? 'orange' : 'muted'}
          defaultOpen={false}
          forceOpen={Boolean(findingError)}
        >
          {renderFindings({ editable: true, showTitle: false })}
        </GeneralJobFoldout>
        </section>

        <section className="general-workspace-panel" hidden={isEdit && workspaceTab !== 'parts'}>
        <GeneralJobFoldout
          title="General Evidence"
          subtitle="Job-level photos and files; item evidence stays with checklist or additional work."
          badge="Optional"
          defaultOpen={false}
        >
          {renderEvidence({ editable: true, showTitle: false })}
        </GeneralJobFoldout>

        <GeneralJobFoldout
          title="Stock / Materials"
          subtitle="Optional job-level stock usage; additional-work stock stays with its request."
          badge="Optional"
          defaultOpen={false}
        >
          {renderStockUsage({ editable: true })}
        </GeneralJobFoldout>
        </section>

        <section className="general-workspace-panel" hidden={isEdit && workspaceTab !== 'admin'}>
        <GeneralJobFoldout
          title="Pricing & Job Admin"
          subtitle="Pricing status, payment actions and private notes."
          badge={priceLabel}
          badgeTone={priceStatus === 'final' ? 'green' : priceStatus === 'quote_required' ? 'orange' : 'blue'}
          defaultOpen={!isEdit}
        >
          <div className="general-job-admin-panel">
            {renderPriceRepair()}
            <VatIntakeTotals intake={vatIntake} />
            {renderPaymentPanel({
              amount: internalJobTotal,
              pricingModeValue: pricingMode,
              priceStatusValue: priceStatus,
              allowActions: false,
            })}

            {renderDiscounts()}
            {renderInvoiceRecords()}
            <div className="grid-3">
              <div className="field">
                <label>Pricing Mode</label>
                <select value={pricingMode} onChange={e => changePricingMode(e.target.value)} disabled={saving || selectedServiceRows.length > 0}>
                  {PRICING_MODE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
                {selectedServiceRows.length > 0 && <div className="field-hint">Pricing follows the selected services.</div>}
              </div>
              <div className="field">
                <label>Price Status</label>
                <select value={priceStatus} onChange={e => setField('price_status', e.target.value)} disabled={saving || selectedPricing?.priceStatus === 'quote_required' || selectedPricing?.priceStatus === 'estimate'}>
                  {PRICE_STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Labour / Known Price (£)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={labourChargeFieldValue}
                  onChange={e => setField('labour_charge', e.target.value)}
                  placeholder="0.00"
                  disabled={saving || selectedServiceRows.length > 0}
                />
                <div className="field-hint">
                  {selectedServiceRows.length > 0
                    ? additionalWorkBillable > 0 ? `Selected services ${serviceSubtotalLabel}; job total ${priceLabel}.` : 'Pricing follows the selected services.'
                    : priceLabel}
                </div>
              </div>
            </div>

            <div className="field">
              <label>Internal Notes</label>
              <textarea value={form.notes} onChange={e => setField('notes', e.target.value)} style={{ minHeight: 90 }} disabled={saving} />
            </div>

            <div className="field">
              <label>Internal Handover / Follow-Up Notes</label>
              <textarea value={form.further_work} onChange={e => setField('further_work', e.target.value)} style={{ minHeight: 90 }} disabled={saving} />
            </div>
          </div>
        </GeneralJobFoldout>
        </section>

        <div className={isEdit ? 'modal-action-bar' : 'action-row'}>
          <button className="btn btn-primary" onClick={save} disabled={saving || vatIntake.blocked || (!isEdit && !canCreateGeneralJob)}>{saving ? 'Saving...' : isEdit ? 'Save changes' : 'Create General Job'}</button>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
        </div>
      </div>
    </div>
  )
}
